// BETA 22.42 — fold socketed weapon-mod buffs into computeWeaponStats. Leaf module
// (no back-import of weapons.ts), so this top-level import is cycle-safe.
import { applyModBuffs } from './weaponModStats';
/**
 * ============================================================
 *  GOOP ZONE — weapons.ts
 *  Weapon Definition Database
 * ============================================================
 *
 *  Static data for every weapon in the game.
 *  Each weapon is a template — runtime instances are created
 *  by weaponSystem.ts when a player equips or levels a weapon.
 *
 *  Drop into: client/src/game/weapons.ts
 * ============================================================
 */

// ─── Weapon Type ─────────────────────────────────────────────
export type WeaponType =
  | 'projectile'    // Fires bullets/arrows/fireballs at enemies
  | 'melee'         // Slash/swing around the player
  | 'orbit'         // Circles around the player
  | 'aoe'           // Area damage around the player
  | 'boomerang'     // Thrown and returns to player
  | 'trail';        // Leaves damage trail behind player

// ─── Weapon Fire Mode ────────────────────────────────────────
export type WeaponFireMode =
  | 'auto_aim'      // Automatically targets nearest enemy
  | 'auto_direction'// Fires in the direction the player is moving
  | 'manual_aim';   // Player aims with mouse/stick (rare)

// ─── Weapon Rarity (for GOOP upgrade system) ────────────────
export type WeaponRarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';

// ─── Projectile Shape ───────────────────────────────────────
// Visual shape of the projectile body
//   'circle' — round bullet (revolver, RPK)
//   'beam'   — long thin streak, oriented along velocity (sniper, lasers)
//   'orb'    — large glowing ball (fireball, magic)
export type ProjectileShape = 'circle' | 'beam' | 'orb' | 'bullet';

// ─── Projectile Definition ──────────────────────────────────
export interface ProjectileDef {
  speed: number;           // Pixels per second
  speedPerLevel: number;   // Added per weapon level
  size: number;            // Radius in pixels
  sizePerLevel: number;    // Added per weapon level
  piercing: number;        // How many enemies it passes through (0 = stops on first hit)
  piercingPerLevel: number;
  bounces: number;         // How many times it bounces to another enemy
  bouncesPerLevel: number; // Added per level (can be fractional — rounds down)
  bounceRange: number;     // Max distance to find next bounce target (px)
  lifetime: number;        // Seconds before projectile despawns
  spriteUrl: string;       // CDN URL for projectile sprite (empty = use fallback circle)
  trailEffect: boolean;    // Whether to draw a trail behind the projectile
  trailColor: string;      // Trail color (CSS color string)

  // ── Visual identity per weapon ──
  projectileShape: ProjectileShape;  // Shape primitive used by renderer
  projectileColor: string;           // Body fill color (CSS)
  projectileGlowColor: string;       // Outer glow / shadow color (CSS)
  projectileLength: number;          // Length in px (only used by 'beam' shape)
  spinRate?: number;                 // Radians/sec the sprite spins while flying (0 = orient along velocity)
  spriteDisplaySize?: number;        // Override render height in world-units (for throwable sprites)
  /** BETA 13.96 — Terminator boomerang. Projectile reverses toward player at
   *  half its lifetime AND clears hitEnemyIds (each enemy gets hit twice). */
  boomerangReturn?: boolean;

  // ── BETA 13.97 — AoE ground effect on projectile impact ──────────────────
  /** When set, projectile spawns a persistent "ground effect" (smoke cloud,
   *  slow puddle) at the impact point. Effect ticks slow + DoT to enemies
   *  inside the radius for `aoeOnImpactDuration` seconds. */
  aoeOnImpactRadius?: number;
  /** Seconds the ground effect persists. */
  aoeOnImpactDuration?: number;
  /** Slow multiplier applied to enemies inside (0.5 = half speed). */
  aoeOnImpactSlowMult?: number;
  /** Damage per second applied to enemies inside (DoT). */
  aoeOnImpactDotPerSec?: number;
  /** Render color for the ground effect (CSS string, alpha 0.0-1.0). */
  aoeOnImpactColor?: string;
  /** BETA 13.101 — Sprite path for the ground effect (overrides color disc).
   *  Used by Shitty Cannon to leave a SHIT/poop floor sprite on impact. */
  aoeOnImpactSpriteUrl?: string;
  /** BETA 14.60 — Stage-2 sprite shown AFTER an enemy first steps onto the
   *  AoE-impact ground effect. Lets poop/sludge weapons swap to a "stepped-on"
   *  splatter visual the moment something walks through. Used by Poop Sling
   *  and Shitty Cannon — impact puddle starts as a clean poop pile sprite,
   *  swaps to the brown-fly puddle sprite on first contact. Mirrors
   *  groundSpriteUrlAfterStep on GroundEffect. */
  aoeOnImpactSpriteUrlAfterStep?: string;

  // ── BETA 22.56 — instant blast on impact (Beer Grenade; RPG in Phase 2) ──
  /** wu — every alive enemy within this radius of the impact point takes an
   *  instant explosion hit (the directly-struck enemy is excluded — it
   *  already took the projectile damage). 0/undefined = no blast. */
  explodeOnImpactRadius?: number;
  /** Explosion damage as a multiple of the projectile's damage (default 1). */
  explodeOnImpactDamageMult?: number;

  // ── BETA 13.101 — Clean sprite render (no procedural glow halo) ──────────
  /** When true, sprite projectile renders without the procedural colored
   *  glow halo around it (just sprite + crit halo on crits only). User
   *  feedback: "remove the pretty color animations on it". Used on Ultra
   *  Beam, Frozen Darts, Terminator, Shitty Cannon, Poop Sling, Ugly Kittens. */
  cleanSprite?: boolean;
}

// ─── Weapon Definition (Static Template) ────────────────────
export interface WeaponDefinition {
  id: string;
  name: string;
  description: string;
  owner: string;           // Character ID that starts with this weapon
  weaponType: WeaponType;
  fireMode: WeaponFireMode;

  // ── Sprite ──
  spriteUrl: string;       // Weapon icon for UI/HUD
  spriteSize: number;      // Display size in pixels (world units = height)
  barrelLength: number;    // World units from orbit slot center to barrel tip (tune per sprite)
  /** BETA 14.10 — Perpendicular offset (world units) from the barrel axis to
   *  the actual barrel-tip position. Applied perpendicular to the aim direction.
   *  Captured by BarrelCalibrator as offsetFromCenterWorldUnits.dy. Defaults
   *  to 0 (barrel on the aim axis). */
  barrelOffsetY?: number;
  /** BETA 14.11 — Source-PNG rotation correction (radians). When the weapon
   *  sprite is drawn at an angle in the source PNG (e.g. Ultra Beam pointing
   *  up-right diagonally), the orbital render rotates by `drawAngle - this`
   *  so the barrel ends up along the aim direction in canvas. Compute as
   *  atan2(calibratorDy, calibratorDx) — the angle the barrel makes with +X
   *  in the source PNG. Default 0 (sprite already faces +X). */
  spriteRotationOffset?: number;
  /** BETA 14.19 — Visual scale multiplier for the orbiting weapon sprite.
   *  Multiplies sprH (and consequently sprW) at draw time so the sprite
   *  appears larger/smaller without changing world-unit collision math
   *  (barrelLength + barrelOffsetY are already calibrated relative to the
   *  scaled sprite via the BarrelCalibrator's scale slider). Default 1.0
   *  (no scaling). User: "give me a rotate and scale slider in the barrel
   *  calibration". */
  spriteScale?: number;

  // ── Base Stats ──
  baseDamage: number;
  damagePerLevel: number;
  baseCritChance: number;      // 0.0 to 1.0
  critChancePerLevel: number;
  baseCritDamage: number;      // Multiplier (e.g., 0.5 = +50% on crit)
  critDamagePerLevel: number;
  baseKnockback: number;       // Knockback force applied to enemies
  knockbackPerLevel: number;

  // ── Fire Rate ──
  baseProjectileCount: number; // Projectiles per burst/attack
  projectileCountPerLevel: number;
  burstDelay: number;          // Seconds between each shot in a burst (0 = all at once)
  baseReloadTime: number;      // Seconds between bursts/attacks
  reloadTimePerLevel: number;  // Subtracted per level (negative = faster)
  minReloadTime: number;       // Floor for reload time

  // ── Spread ──
  spreadAngle: number;         // Degrees of spread for multi-projectile weapons
  spreadPerLevel: number;      // Change per level (negative = tighter)

  // ── Projectile ──
  projectile: ProjectileDef;

  // ── Leveling ──
  maxLevel: number;

  // ── Unlock ──
  unlockedByDefault: boolean;
  unlockQuest: string;         // Quest description (empty if unlocked by default)
  goopUnlockCost: number;      // GOOP cost to unlock without quest

  // ── Lock-On Range ──
  // Maximum world-unit distance at which this weapon will auto-target an enemy.
  // Should reflect the weapon's personality — rifle locks on from far, spatula needs
  // the enemy basically on top of you. Keep values ≤ the projectile's natural range
  // (speed × lifetime) so projectiles actually reach their targets.
  lockOnRange: number;

  // ── BETA 16.69 — Trap-mode LURE stats (Ugly Kittens monkey-bomb mode) ──
  // When set, projectiles spawned by this weapon ATTRACT enemies within
  // lureRadius. Enemies steer toward the projectile instead of the
  // player while inside this radius. maxLured caps the number of enemies
  // a single trap can attract at once. Both scale with weapon level.
  // See kittenLure.ts for the per-frame assignment logic.
  baseLureRadius?: number;        // wu — pulls enemies in this radius (0 = no lure)
  lureRadiusPerLevel?: number;    // wu added per weapon level
  baseMaxLured?: number;          // max enemies attracted by a single placement
  maxLuredPerLevel?: number;      // additional max-lured per weapon level

  // ── Throwable flag ──
  // When true the projectile sprite IS the weapon (axe, basketball, etc.)
  // The HUD hides the "X / Y" ammo counter and shows THROW READY / CHARGING instead.
  throwable?: boolean;

  // BETA 19.4 — Megabonk-style level-up stat pool. When set, weapon
  // level-up cards roll 1-2 stats from this pool (common = 1 stat,
  // uncommon+ = 2 stats). When NOT set, the pool is auto-derived from
  // non-zero *PerLevel fields on this weapon — so weapons without an
  // explicit pool still feel right by default. To tighten a weapon's
  // identity (e.g. Spatula is melee, so don't roll projectileSpeed), set
  // an explicit pool that excludes irrelevant stats.
  levelUpStatPool?: WeaponStatKey[];

  // ── BETA 14.94 — Continuous orbit spin speed (rad/sec) ──
  // The sprite rotates on its OWN center as it orbits the player. This is a
  // VISUAL effect only — does NOT affect projectile direction or aim mode.
  // Default 0 (no spin) for projectile bullets. Throwables default to a
  // non-zero value (e.g. 6 rad/sec for axe-style spin) since they ARE the
  // projectile and should look like they're spinning in the air. Editable
  // per weapon in the Combat Test Bench so any weapon can opt into spin.
  orbitSpinSpeed?: number;

  // ── BETA 14.95 — Multi-barrel definitions ──
  // When the user places multiple arrows in the calibrator, each becomes one
  // barrel. The bench fires ONE projectile per barrel per fire cycle (so a
  // 2-arrow weapon fires 2 simultaneous bullets, one from each calibrated
  // tip). User: "if i add two arrows then two projectile of that wepons ammo
  // will fire. some wepons have more than one barrel."
  // length / offsetY / angle have the same meaning as the legacy single-arrow
  // barrelLength / barrelOffsetY / spriteRotationOffset fields. Arrow #0
  // backfills the legacy fields for any code path that hasn't been updated
  // to read barrels[] directly.
  barrels?: Array<{ length: number; offsetY: number; angle: number }>;

  // ── Aura flag ──
  // When true this weapon creates a damage ring around the player instead of projectiles.
  // projectile.size  = ring radius in world units (scales with level via sizePerLevel)
  // reloadTime       = cooldown between pulses
  // damage           = damage dealt to every enemy touching the ring on each pulse
  // The weapon icon still shows in the HUD; no orbit sprite is drawn.
  aura?: boolean;
  /** BETA 22.72 — Firestarter: the continuous muzzle CONE is the weapon (no projectiles). */
  flameCone?: boolean;

  // ── Arc flag ──
  // When true this weapon fires a fan of instant piercing projectiles in the direction
  // the player is currently moving (facing angle), like a melee slash.
  // spreadAngle      = total arc width in degrees (e.g. 110)
  // baseProjectileCount = number of rays in the fan (spread evenly across the arc)
  // projectile.lifetime × projectile.speed = melee reach in world units
  // projectile.piercing should be 99 — hits EVERY enemy in the arc per swing
  // No target lock-on required — fires even with no enemies in range.
  // No orbit sprite is drawn; a visual arc flash is shown on each swing.
  arc?: boolean;

  // ── Instant-hit flag (BETA 13.44) ──
  // When true the locked-on auto-aim target takes damage AT FIRE TIME (not when the
  // projectile arrives). The visual projectile still spawns and travels normally,
  // and may piercing-hit OTHER enemies on its way — but the originally-locked target
  // is guaranteed to be hit even if it dies / moves out of bounds before the
  // projectile would have reached it. Use for snipers and other "every shot lands"
  // weapons. Only meaningful for weaponType === 'projectile' && fireMode === 'auto_aim'.
  instantHit?: boolean;

  // ── BETA 13.95 — Status effects on hit ──────────────────────────────────────
  // When set, weapon applies the named status to enemies it damages.
  // Works on bosses too. Stacking: longer of (current timer, new duration).

  /** Duration in seconds to lock the enemy in place. (BETA 21.35 removed the old Ugly Kittens
   *  freeze — kittens are now pure chase+claw damage pets, no freeze. Currently unused.) */
  applyFreezeOnHit?: number;
  /** Duration in seconds the enemy moves at slowOnHitMultiplier speed. */
  applySlowOnHit?: number;
  /** Slow multiplier applied while slowedUntil > 0. 0.5 = half speed. Default 0.5. */
  slowOnHitMultiplier?: number;
  /** Duration in seconds the enemy is WET (Phase 3: 1.5× damage from electric). */
  applyWetOnHit?: number;
  /** Heal player by (damage_dealt × lifestealPct) per hit. 0.08 = 8%. */
  lifestealPct?: number;

  // ── BETA 13.95 — Animated projectile sprite (Ugly Kittens) ──────────────────
  /** Cycle through these sprite URLs in flight (frame swap every animFrameDuration s).
   *  Used by Ugly Kittens for the atk2/atk3/atk4 attack-frame cycle. */
  projectileAnimFrames?: string[];
  /** Seconds per frame in projectileAnimFrames. Default 0.15. */
  projectileAnimFrameDuration?: number;

  // ── BETA 13.96 — Fired weapon sprite swap (Poop Sling loaded → fired anim) ─
  /** When set, weapon icon swaps to this sprite for `firedSpriteDuration` seconds
   *  on each shot. Used by Poop Sling: idle = loaded pouch, brief flash = fired. */
  firedSpriteUrl?: string;
  /** Seconds the firedSpriteUrl is shown after each shot. Default 0.18. */
  firedSpriteDuration?: number;

  // ── BETA 13.96 — Orbital companion (Karen Drones flying out) ────────────────
  /** When true, the weapon sprite flies out from the player at orbitRadius, orbiting
   *  at orbitSpeed rad/s. Projectiles spawn from the orbital position (not player center).
   *  Karen Drones uses this with 4 spinning propellers overlaid. */
  orbital?: boolean;
  /** World-unit radius from player center for the orbital weapon. Default 90. */
  orbitRadius?: number;
  /** Radians per second for orbital rotation. Default 1.6. */
  orbitSpeed?: number;
  /** When true, render 4 spinning propeller plus-shapes overlaid on the weapon sprite
   *  (positioned at wrist + ankle offsets). Karen Drones only. */
  orbitalPropellers?: boolean;
  /** BETA 13.100 — When true, the orbital weapon damages enemies on CONTACT
   *  with its position (no projectiles fired). Used by Karen Drones — she IS
   *  the projectile, deals damage when the drone touches an enemy. */
  orbitalContactDamage?: boolean;
  /** Contact damage radius in world units (around the orbital position). Default 32. */
  orbitalContactRadius?: number;
  /** Per-enemy contact-damage cooldown in seconds (so we don't tick-spam on touch). */
  orbitalContactCooldown?: number;
  /** BETA 20.06 — SECONDARY contact damage for orbital weapons that ALSO fire
   *  projectiles (Karen Drones). Unlike orbitalContactDamage (which is the sole
   *  attack + skips projectile firing), this runs alongside the projectile path.
   *  Value is a multiplier on weapon damage per touch (e.g. 0.5 = 50% of per-shot
   *  damage when a drone body touches an enemy). Per Dylan: "i love how they spin
   *  in a circle fast as a secondary attack. i dont think any enemy took damage
   *  just by the drones touching them." */
  orbitalSecondaryContactDamage?: number;

  // ── BETA 13.97 — Armor / shield strip on hit (Ultra Beam) ───────────────────
  /** Flat amount of shieldHP stripped per hit (aura tick or projectile contact). */
  shieldStripPerHit?: number;
  /** Flat amount of armor stripped per hit. */
  armorStripPerHit?: number;

  // ── BETA 15.74 — Charge-up before fire (Ultra Beam) ─────────────────────────
  /** Milliseconds the weapon "winds up" between trigger and projectile spawn.
   *  Used by Ultra Beam to align the projectile with the BLAM in the audio
   *  file (audio plays at trigger; projectile spawns at chargeUpMs after).
   *  Default: 0 (instant fire). */
  chargeUpMs?: number;

  // ── BETA 14.96 — User vertical flip toggle ──────────────────────────────────
  /** When true, the weapon sprite is mirrored vertically in the bench/render
   *  (combines with the orbit's auto-flip-when-pointing-left). Editable via
   *  the bench Sprite section. Defaults to false. */
  spriteFlipV?: boolean;

  // ── BETA 13.99 — Sprite faces left in source PNG ────────────────────────────
  /** When true, the weapon sprite is drawn LEFT-facing in the asset; flip
   *  horizontally so the barrel points right. Used by Frozen Darts +
   *  Ex Girlfriend whose source PNGs face left. */
  spriteFacesLeft?: boolean;

  // ── BETA 13.101 — Arc weapon variants (Vampire Fang) ─────────────────────────
  /** When true, do NOT mirror the arc sprite horizontally. Spatula needs the
   *  mirror (sprite faces left in source); Vampire Fang sprite faces right in
   *  source so mirroring inverts it backwards. Default behavior = mirror. */
  arcSpriteNoFlip?: boolean;
  /** When true, the arc swing eases from slow at start to fast at end (ramp-up).
   *  Default = linear sweep. */
  arcRampUpSwing?: boolean;
  /** When true, the arc sprite stays at full alpha for the entire swing
   *  (no fade-out). Default = fade in last 45% of swing. */
  arcNoFade?: boolean;

  // ── BETA 19.44 — Arc weapon rework (Dylan spec, live audit) ────────────────
  // Two melee weapons (Spatula, Vampire Fang) now use SPRITE-COLLISION hit
  // detection — the swinging sprite IS the hitbox, no more invisible-ray
  // companions. Each weapon picks its own "personality" via these flags.
  /** When true, the swing centerAngle is REFRESHED to player.facingAngle every
   *  frame so the swing follows the player's current movement direction. Used
   *  by Spatula (dumb melee — points wherever you're walking). Without this
   *  the centerAngle is sampled at swing start and locked. Vampire Fang leaves
   *  this off so its auto-aim direction can't drift mid-swing. */
  arcLiveDirection?: boolean;
  /** When true, the swing centerAngle is computed at swing-start by finding
   *  the nearest alive enemy and pointing toward it. Used by Vampire Fang
   *  (smart blade — auto-targets the closest threat). Without this the
   *  centerAngle defaults to player.facingAngle at swing-start. */
  arcAutoAim?: boolean;
  /** When true, the swing applies the SAME full-strength knockback to every
   *  enemy the sprite touches (no falloff by distance from sprite center).
   *  Used by Spatula — specializes in scattering hordes. */
  arcAoEKnockback?: boolean;
  /** Per-level flat growth to the arc sprite size. Used to make the blade /
   *  spatula visibly LONGER as the weapon levels. The sprite collision uses
   *  the same effective size so the hitbox grows in step. */
  arcSpriteSizePerLevel?: number;
  /** Cap (in degrees) for spreadAngle after spreadPerLevel growth. Defaults
   *  to 360 — when reached, the swing flips to continuous-orbit mode (sprite
   *  spins around the player without fading). */
  arcMaxSpreadDeg?: number;
}

// BETA 19.4 — Stat keys that can roll on a weapon level-up card. Maps
// directly to the *PerLevel fields on WeaponDefinition. computeWeaponStats
// only applies the *PerLevel delta for stats that were actually rolled at
// that level (per cardRollsHistory). Auto-derived from non-zero *PerLevel
// fields if a weapon doesn't define an explicit levelUpStatPool.
export type WeaponStatKey =
  | 'damage'
  | 'critChance'
  | 'critDamage'
  | 'knockback'
  | 'reload'
  | 'projectileCount'
  | 'spread'
  | 'projectileSpeed'
  | 'projectileSize'
  | 'projectilePiercing'
  | 'projectileBounces';

// BETA 19.4 — Per-level card roll. Captures BOTH the rolled rarity AND
// the stat(s) that rolled. computeWeaponStats walks this to apply the
// correct deltas for each level the weapon has reached.
// BETA 19.14 — Added per-level luckMult so the player's luck AT ROLL TIME
// scales the magnitude of that level's stat boost. Locked in per-level
// (not re-computed retroactively when luck changes later in the run).
export interface WeaponCardRoll {
  rarity: import('./peanutPercs').UpgradeRarity;
  stats: WeaponStatKey[];
  /** BETA 19.14 — Luck-driven magnitude multiplier captured at roll time.
   *  Optional for backward-compat with 19.4-19.13 saves (defaults to 1.0). */
  luckMult?: number;
  /** BETA 21.02 — per-card buff-variety JITTER captured at roll time (≈±15%,
   *  luck-biased). Optional → defaults to 1.0 for ALL pre-21.02 saves (no
   *  change to old replays). Applied alongside luckMult in computeWeaponStats. */
  jitterMult?: number;
}

// ─── Weapon Instance (Runtime State) ────────────────────────
export interface WeaponInstance {
  definitionId: string;
  level: number;
  // BETA 23.01 — smart-slide offset (weapon depth system); lerped each tick
  slideX?: number;
  slideY?: number;
  rarity: WeaponRarity;       // Current GOOP-upgraded rarity (pre-19.2 system; still drives the global-weapon rarity multiplier).

  // BETA 19.2 — Megabonk-style per-level card rarity. Index 0 = base
  // level (always 'common'). Index N = card rolled at level N+1.
  // Kept for backward compat — see cardRollsHistory below for the new
  // per-stat-pool data.
  cardRarityHistory?: import('./peanutPercs').UpgradeRarity[];

  // BETA 19.4 — Full per-level roll history (rarity + which stats rolled).
  // Each index N corresponds to the card picked at level N+1. Index 0 is
  // the base level so always has empty stats. When present, this REPLACES
  // cardRarityHistory as the source of truth — computeWeaponStats reads
  // it to apply only the *PerLevel deltas for stats that were rolled at
  // each level. When absent (legacy 19.0-19.2 saves), falls back to
  // cardRarityHistory behavior (all stats scale by per-level rarity).
  cardRollsHistory?: WeaponCardRoll[];

  // ── Computed Stats (recalculated on level-up) ──
  damage: number;
  critChance: number;
  critDamage: number;
  knockback: number;
  projectileCount: number;
  reloadTime: number;
  spreadAngle: number;
  projectileSpeed: number;
  projectileSize: number;
  projectilePiercing: number;
  projectileBounces: number;

  // ── Runtime State ──
  currentAmmo: number;         // Shots remaining in current burst
  reloadTimer: number;         // Countdown until next burst
  burstTimer: number;          // Countdown until next shot in burst
  isFiring: boolean;           // Currently in a burst
  isReloading: boolean;        // Waiting for reload

  // ── Per-weapon telemetry (for bot data collection) ──
  // weaponTelemetry CSV format: `dmgX:hitsA/shotsB:critsC:critRollsD:tickEventsE`
  //   hitsA       = totalShotsHit (target-hit events; can exceed shots for piercing/AoE)
  //   shotsB      = totalShotsFired (projectile shots; for orbital/aura/beam see tickEvents)
  //   critsC      = totalCrits (target-hit events that were crit; can exceed crit-shot count for piercing)
  //   critRollsD  = totalCritRolls (BETA 14.30 — # of SHOTS that rolled crit; ≤ shotsFired)
  //   tickEventsE = totalTickEvents (BETA 14.30 — # of aura pulses / orbital zaps / beam segments;
  //                                   distinct from shotsFired for accuracy math on non-projectile weapons)
  totalDamageDealt: number;    // Cumulative damage dealt by this weapon
  totalShotsFired: number;     // Total projectile shots fired (use tickEvents for orbital/aura/beam)
  totalShotsHit: number;       // BETA 13.43: target-hit events (sum across pierce/AoE). Hit rate = hits / (shots + tickEvents) when relevant.
  totalCrits: number;          // BETA 14.30 (BUG-04 doc) — # of crit target-HITS, not shots. Can exceed shotsFired with piercing.
  /** BETA 14.30 (BUG-01) — # of shots that rolled crit (one per crit-shot, not
   *  per piercing-target). Use this for "crit chance proc rate" math:
   *  totalCritRolls / totalShotsFired ≤ player.critChance. */
  totalCritRolls: number;
  /** BETA 14.30 (BUG-03/04) — # of tick events for non-projectile weapons:
   *  aura pulses (Chad Aura), orbital zaps (Karen Drones, Iranian Bird,
   *  Bungulators auto-attacks), beam segments (Ultra Beam). Use this in place
   *  of shotsFired for accuracy / crit-rate math on those weapon types. */
  totalTickEvents: number;

  // ── Visual aim state (for on-screen orbit weapon rendering) ──
  aimAngle: number;            // Current aim angle (radians); rotates toward target
  hasTarget: boolean;          // Whether currently aiming at an enemy

  // ── Aura weapon runtime state (only used when def.aura === true) ──
  auraTick: number;            // Seconds remaining until next aura pulse (visual/telemetry beat)
  auraPulseTimer: number;      // Flash effect countdown (0.25s after each pulse)
  // BETA — PER-ENEMY damage cooldowns: enemyId → seconds until that enemy can be
  // hit again. A new enemy (no entry) is hit IMMEDIATELY on contact, then on its
  // own interval — so fast enemies can't slip through the gap between ticks.
  auraEnemyCd?: Map<number, number>;

  // ── BETA 13.96 — Fired sprite flash timer (Poop Sling loaded ↔ fired) ──
  /** When > 0, render def.firedSpriteUrl instead of def.spriteUrl.
   *  Set to def.firedSpriteDuration on each shot. */
  firedFlashTimer: number;

  // ── BETA 22.69 — Weapon Feel: render-only kick/bob state ──
  /** Counts down from WeaponVisualConfig.recoilDuration after each shot;
   *  renderOrbitWeapons offsets the sprite backward along -aim while > 0.
   *  Optional so pre-22.69 rehydrated instances default via `?? 0`. */
  recoilTimer?: number;
  /** Per-instance phase offset so idle bobs don't sync across slots. */
  idleBobPhase?: number;

  // ── BETA 13.100 — Per-enemy contact-damage cooldowns (Karen Drones) ──
  /** Map<enemyId, secondsRemaining> — used by orbitalContactDamage to prevent
   *  spamming damage every frame the drone overlaps a stationary enemy. */
  contactCooldowns: Map<number, number>;

  // ── BETA 15.74 — Charge-up state (Ultra Beam) ──────────────────────────────
  /** Seconds remaining in the current charge-up. > 0 means we're winding up
   *  audio + locked-aim and have NOT spawned a projectile yet. Decrements
   *  each frame; when it hits 0 the projectile spawns at the locked aim. */
  chargeUpRemaining: number;
  /** Aim angle locked at trigger moment (radians). Used by Ultra Beam so
   *  enemies can't juke during the 0.737s charge-up. */
  chargeUpLockedAimAngle: number;
  /** BETA 22.86 — the full charge-up window (seconds) captured when the wind-up
   *  starts, so the render can show a precise 0→1 charge-glow ramp at the muzzle
   *  (Railgun/Ultra Beam). 0 when not charging. Pure VFX bookkeeping. */
  chargeUpTotal?: number;
}

// ─── Rarity Multipliers (from GOOP weapon upgrades) ─────────
export const RARITY_MULTIPLIERS: Record<WeaponRarity, number> = {
  common:    1.0,
  uncommon:  1.15,
  rare:      1.30,
  epic:      1.50,
  legendary: 1.75,
};

// ═══════════════════════════════════════════════════════════════
//  WEAPON DATABASE
// ═══════════════════════════════════════════════════════════════

export const WEAPON_DATABASE: WeaponDefinition[] = [

  // ─────────────────────────────────────────────────────────────
  //  REVOLVER — Cowboy Peanut's Starter Weapon
  // ─────────────────────────────────────────────────────────────
  {
    id: 'revolver',
    // BETA 16.27 — PSBOT cycle-29 / Dylan: "should be called revolver."
    // Was 'Six Shooter' which appeared 47× in offering data alongside
    // 'The Revolver' (18×) — two display names for the same weapon was
    // confusing. Unifying to "The Revolver" per Dylan.
    name: 'The Revolver',
    description: 'A trusty revolver. Starts methodical — upgrade it and watch it sing.',
    owner: 'cowboy_peanut',
    weaponType: 'projectile',
    fireMode: 'auto_aim',

    spriteUrl: '/weapons/weapon_03_revolver.png',
    spriteSize: 28,
    // BETA 14.10 — calibrator (user-marked barrel tip): dx 23.45, dy -6.3
    barrelLength: 23.45,
    barrelOffsetY: -6.3,

    // ── Base Stats ──
    baseDamage: 36,               // BETA 19.36 — Dylan: "cowboy peanut revolver should only take 2 shots to kill a zombie at start of game". Was 25 (set in 15.1 for "2-hit kill on 50-HP zombie") but BETA 16.91 doubled enemy HP globally (ENEMY_HP_GLOBAL_MULT = 2.0). Effective zombie HP at run start = 50 × 2 = 100. New baseDamage 50 → 2 shots to kill. TTK 2.0s at the 1.0s reload preserved per the original spec.
    damagePerLevel: 5,            // keeps pace with enemy HP scaling
    baseCritChance: 0.05,
    critChancePerLevel: 0.08,
    baseCritDamage: 0.5,
    critDamagePerLevel: 0.15,
    baseKnockback: 2,
    knockbackPerLevel: 0.3,

    // ── Fire Rate ──
    baseProjectileCount: 1,
    projectileCountPerLevel: 1,
    burstDelay: 0.30,             // BETA 15.30: 0.45 → 0.30 per Dylan's tier-list. Cowboy avg 68s / 46 kills. At lvl5 the 5-shot burst was 2.25s long — too slow to clear approaching zombies. Snappier cadence keeps the revolver "draw" feel without changing base damage.
    baseReloadTime: 1.0,          // full reload between bursts for deliberate feel
    reloadTimePerLevel: -0.06,
    minReloadTime: 0.30,

    // ── Spread ──
    spreadAngle: 8,               // Tight 8-degree cone
    spreadPerLevel: -0.1,         // Gets slightly tighter

    // ── Projectile ──
    projectile: {
      speed: 1400,                  // BETA 15.1 — user direction: most guns at sniper-baseline 1400 (felt nice in 50-run cyc-13). Was 500.
      speedPerLevel: 40,
      size: 4,
      sizePerLevel: 0.2,
      piercing: 0,                // Stops on first enemy hit
      piercingPerLevel: 0,
      bounces: 0,
      bouncesPerLevel: 1,         // BETA 15.24 — Dylan: lowest +1
      bounceRange: 150,           // Max distance to find bounce target
      lifetime: 1.5,              // Despawns after 1.5 seconds
      spriteUrl: '/weapons/bullet_revolver.png',
      trailEffect: true,
      // BETA 20.72 — clean-GOLD tracer (VFX identity: Revolver = THE clean
      // single shot vs RPK's orange stream). Renders via the new streak-trail
      // branch in weaponSystem (gold line + white-hot core), not dots/ghosts.
      trailColor: '#FFD700',
      projectileShape: 'bullet',
      projectileColor: '#C8A030',
      projectileGlowColor: '#FFE066', // BETA 20.72 — brighter gold halo (was dull #FFD060)
      projectileLength: 0,
    },

    // BETA 13.54: revolver also lands on locked target. Cycle 13 CSV showed
    // ~44% hit rate — same overkill pattern as the sniper before instantHit
    // (target died from another weapon between fire frame and bullet arrival).
    instantHit: true,

    // ── Leveling ──
    maxLevel: 40,

    // ── Unlock ──
    unlockedByDefault: true,      // Cowboy Peanut starts with it
    unlockQuest: '',
    goopUnlockCost: 0,
    lockOnRange: 300,  // mid-range handgun — enemies visible, closing in
  },

  // ─────────────────────────────────────────────────────────────
  //  PEANUT RIFLE — Tactical Peanut's Starter Weapon
  // ─────────────────────────────────────────────────────────────
  {
    id: 'peanut_rifle',
    name: '.50 Cal Sniper',
    description: 'Single high-damage piercing shot. Slow but devastating. One in the chamber.',
    owner: 'tactical_peanut',
    weaponType: 'projectile',
    fireMode: 'auto_aim',

    spriteUrl: '/weapons/wpn_peanut_rifle_solid.png',
    spriteSize: 38,
    // BETA 14.10 — calibrator: dx 32.77, dy -2.69
    barrelLength: 32.77,
    barrelOffsetY: -2.69,

    // ── Base Stats ──
    baseDamage: 37,                  // BETA 20.03 — Dylan: "the sniper should be a 1 shot one kill at the start of the game." Was 51 vs 50 HP zombie (one-shot but tight). Bumped to 60 for safety margin (covers per-minute scaling at minute 1: 50 × 1.7 = 85 → still 2-shot, but consistent one-shot vs all minute-0 zombies). Originally 35→51 in BETA 13.31 for the same intent.
    damagePerLevel: 14,              // BETA 17.61 — Dylan: "sniper should kill in 2 shots easy."
                                     // Was 8/level: LV 22 had baseDmg 227 → enemies at 600 HP took 3+ shots.
                                     // Now 14/level: LV 22 baseDmg 359 → 2-shot the 600 HP enemies. Crits make 1-shot common.
    baseCritChance: 0.20,            // High base crit (sniper passive)
    critChancePerLevel: 0.04,
    baseCritDamage: 1.0,             // Crit doubles damage
    critDamagePerLevel: 0.10,
    baseKnockback: 6,
    knockbackPerLevel: 0.4,

    // ── Fire Rate ──
    baseProjectileCount: 1,          // Bolt-action: one shot per cycle
    projectileCountPerLevel: 0,
    burstDelay: 0,
    baseReloadTime: 1.0,             // BETA 13.33: 1.8 → 1.0 (cycle-2 bot died during reloads with no return fire)
    reloadTimePerLevel: -0.05,
    minReloadTime: 0.5,              // BETA 13.33: 0.7 → 0.5 (matches faster base)

    // ── Spread ──
    spreadAngle: 0,                  // Pinpoint accuracy
    spreadPerLevel: 0,

    // ── Projectile ──
    projectile: {
      speed: 1400,                   // Very fast — almost hitscan
      speedPerLevel: 60,
      size: 3,
      sizePerLevel: 0.1,
      piercing: 2,                   // Passes through 2 enemies before stopping
      piercingPerLevel: 1,           // BETA 15.24 — Dylan: "lowest should be +1, not .1"
      bounces: 0,
      bouncesPerLevel: 0,
      bounceRange: 0,
      lifetime: 1.0,
      spriteUrl: '',
      trailEffect: true,
      trailColor: '#88E0FF',         // Cool blue tracer
      projectileShape: 'beam',
      projectileColor: '#CFEFFF',    // Pale blue-white beam
      projectileGlowColor: '#3AB6FF',
      projectileLength: 60,          // Long thin streak
    },

    // ── Leveling ──
    maxLevel: 40,

    // ── Unlock ──
    unlockedByDefault: true,
    unlockQuest: '',
    goopUnlockCost: 0,
    lockOnRange: 400,  // longest range — sniper locks on from far side of screen

    // BETA 13.44: snipers always land on their locked target. Damage applies at fire
    // time; the visual projectile still travels and can piercing-hit other enemies.
    instantHit: true,
  },

  // ─────────────────────────────────────────────────────────────
  //  SPATULA SLASH — Sponge Peanut's Starter Weapon
  //  Wide directional arc melee swing in the direction you're moving.
  //  Hits EVERY enemy in the sweep — zero target lock-on required.
  // ─────────────────────────────────────────────────────────────
  {
    id: 'spatula_slash',
    name: 'Barbed Wire Bat',
    description: 'Swings a barbed-wire bat in a wide arc. Hits EVERYTHING in the sweep. Move to aim.',
    owner: 'spongebob_peanut',
    weaponType: 'melee',
    fireMode: 'auto_direction',
    arc: true,               // directional arc melee — fires fan in player's facing direction

    spriteUrl: '/weapons/wpn_barbed_bat_solid.png',
    spriteSize: 55,
    barrelLength: 0,         // no barrel — melee weapon

    baseDamage: 30,               // BETA 14.73 cycle-9: 15 → 30 (+100%). Sponge median 1 kill in 24s = 0 DPS, totally broken. Spatula needs to actually horde-clear as a starter melee.
    damagePerLevel: 4,
    baseCritChance: 0.10,
    critChancePerLevel: 0.06,
    baseCritDamage: 0.5,
    critDamagePerLevel: 0.10,
    baseKnockback: 9,             // BETA 19.44 — Dylan: spatula specializes in horde KB. Up 5 → 9.
    knockbackPerLevel: 0.6,       // BETA 19.44 — Dylan: heavier per-level KB growth. Up 0.3 → 0.6.

    baseProjectileCount: 1,       // BETA 19.44 — invisible rays REMOVED; one swing = one sprite hitbox. Count cosmetic only.
    projectileCountPerLevel: 0,
    burstDelay: 0,
    baseReloadTime: 0.51,    // BETA 14.73 cycle-9: 0.76 → 0.51 (1.5× swing speed). Spatula meant to be horde-cleave; pair with 2× damage.
    reloadTimePerLevel: -0.015,
    minReloadTime: 0.28,

    spreadAngle: 110,
    spreadPerLevel: 13,           // BETA 19.44 — Dylan: swing arc grows wider per level → 360° around L20.

    projectile: {
      speed: 650,            // 650 × 0.10 = 65wu reach — solid melee range
      speedPerLevel: 10,
      size: 8,               // large hitbox per ray for reliable melee contact
      sizePerLevel: 0.2,
      piercing: 99,          // pierce everything — melee hits ALL enemies in arc
      piercingPerLevel: 0,
      bounces: 0,
      bouncesPerLevel: 0,
      bounceRange: 0,
      lifetime: 0.10,        // near-instant — this IS the reach (speed × lifetime)
      spriteUrl: '',         // no flying sprite — arc flash handles the visual
      trailEffect: false,
      trailColor: '',
      projectileShape: 'circle',
      projectileColor: '',
      projectileGlowColor: '',
      projectileLength: 0,
    },

    maxLevel: 40,
    unlockedByDefault: true,
    unlockQuest: '',
    goopUnlockCost: 0,
    lockOnRange: 65,  // reference only — arc weapons don't use target lock-on

    // BETA 19.44 — Dylan spec: Spatula points where you're MOVING (live re-aim),
    // sprite IS the hitbox (no rays), and EVERY enemy the sprite touches gets
    // full knockback (no falloff). This is the AOE-knockback specialist.
    arcLiveDirection: true,
    arcAoEKnockback: true,
    arcSpriteSizePerLevel: 1.6,   // blade grows ~32wu over 20 levels (55 → ~87)
    arcMaxSpreadDeg: 360,
  },

  // ─────────────────────────────────────────────────────────────
  //  RPK MACHINE GUN — Killa Peanut's Starter Weapon
  //  Relentless stream of low-damage rounds. Spray and pray.
  // ─────────────────────────────────────────────────────────────
  {
    id: 'rpk_machine_gun',
    name: 'RPK Machine Gun',
    description: 'Full-auto suppression fire. Every shot is weak. Together they shred.',
    owner: 'killa_peanut',
    weaponType: 'projectile',
    fireMode: 'auto_aim',

    spriteUrl: '/weapons/wpn_rpk_solid.png',
    spriteSize: 36,
    // BETA 14.10 — calibrator: dx 31.5, dy -5.55
    barrelLength: 31.5,
    barrelOffsetY: -5.55,

    baseDamage: 17,               // BETA 15.1: 6 → 17. 3-hit kill on 50-HP zombie at 0.14s reload (TTK 0.42s — fastest in the game). User: "fast cooldown = 3 hits". RPK is the high-RoF cleaner.
    damagePerLevel: 3,            // BETA 20.66 — 1.2 → 3 (Dylan-approved, Charter §1: basics ≤4 hits ANY minute). At 1.2/lvl, L10 = 27.8/hit = 8-hit zombies by min 8 while sniper-class stayed fine. At 3/lvl, L10 = 44/hit keeps 3-5-hit kills through min 8+.
    baseCritChance: 0.03,
    critChancePerLevel: 0.03,
    baseCritDamage: 0.3,
    critDamagePerLevel: 0.08,
    baseKnockback: 1,
    knockbackPerLevel: 0.1,

    baseProjectileCount: 1,
    projectileCountPerLevel: 0,
    burstDelay: 0,
    // BETA 21.38 — Dylan: too fast off the rip. 0.14 (~7.1/sec) → 0.28 (~3.6/sec start) —
    // still a machine gun, just not a turret at Lv1; steeper ramp (-0.003 → -0.008)
    // climbs back to its 0.08 floor (~12/sec) by ~Lv25.
    // BETA 21.40 — steeper ramp so the machine gun reaches full rate by ~Lv8;
    // 3.3/s @Lv1 → 12.5/s @Lv8. Slow level-1 start preserved.
    baseReloadTime: 0.30,
    reloadTimePerLevel: -0.035,
    minReloadTime: 0.08,         // hard floor raised — prevents perks from making it absurd

    spreadAngle: 14,
    spreadPerLevel: -0.2,

    projectile: {
      speed: 1400,                  // BETA 15.1: 620 → 1400 (sniper baseline).
      speedPerLevel: 20,
      size: 3,
      sizePerLevel: 0.1,
      piercing: 0,
      piercingPerLevel: 0,
      bounces: 0,
      bouncesPerLevel: 1,         // BETA 15.24 — Dylan: lowest +1
      bounceRange: 120,
      lifetime: 1.2,
      spriteUrl: '',
      // BETA 20.72 — ORANGE tracer ON (was a dead trailColor behind
      // trailEffect:false). Full-auto now reads as a continuous STREAM —
      // drastically different from the Revolver's clean gold single tracer.
      trailEffect: true,
      trailColor: '#FF7A1A',
      projectileShape: 'bullet',
      projectileColor: '#FF8833',
      projectileGlowColor: '#FF4400',
      projectileLength: 0,
    },

    maxLevel: 40,
    unlockedByDefault: true,
    unlockQuest: '',
    goopUnlockCost: 0,
    lockOnRange: 320,  // spray-and-pray — medium range, wide spread
  },

  // ─────────────────────────────────────────────────────────────
  //  FIRESTARTER — flamethrower. Continuous flame cone; the cone IS
  //  the weapon (def.flameCone). No projectiles exist for this weapon.
  //  (id 'fireball_wand' is FROZEN — telemetry/save/bot continuity.)
  // ─────────────────────────────────────────────────────────────
  {
    id: 'fireball_wand',
    name: 'Firestarter',
    description: 'A fire extinguisher rebuilt to START fires. Sprays a continuous cone of flame that cooks everything in it.',
    owner: 'wizard_peanut',
    weaponType: 'projectile',
    fireMode: 'auto_aim',

    spriteUrl: '/weapons/wpn_flamethrower_solid.png',
    spriteSize: 32,
    // BETA 14.10 — calibrator: dx 21.6, dy 0
    barrelLength: 24,   // BETA 22.55 — retuned for the extinguisher nozzle (1.66 aspect crop)

    baseDamage: 37,               // per-touch damage of the flame cone (per-enemy cooldown = reloadTime)
    damagePerLevel: 12,
    baseCritChance: 0.10,
    critChancePerLevel: 0.06,
    baseCritDamage: 0.8,
    critDamagePerLevel: 0.12,
    baseKnockback: 10,
    knockbackPerLevel: 0.6,

    baseProjectileCount: 1,
    projectileCountPerLevel: 0,
    burstDelay: 0,
    baseReloadTime: 1.4,          // BETA 15.30: 2.0 → 1.4 per Dylan's tier-list — wizard avg 49s / 11 kills (worst-2). Slow reload was preventing bot from clearing zombie waves before being overwhelmed. 1.4s = 50% faster cadence.
    reloadTimePerLevel: -0.06,
    minReloadTime: 0.8,

    spreadAngle: 0,
    spreadPerLevel: 0,

    // BETA 22.74 — INERT STUB (Dylan: "forget the fireball ever existed").
    // flameCone weapons NEVER spawn projectiles — update() returns from the
    // cone branch before any fire logic. This block exists only because the
    // def type requires it (48 call sites read def.projectile; making it
    // optional is not worth the blast radius). Values are meaningless.
    projectile: {
      speed: 1400,
      speedPerLevel: 12,
      size: 12,
      sizePerLevel: 0.4,
      piercing: 1,
      piercingPerLevel: 1,           // BETA 15.24 — Dylan: lowest +1, not .1
      bounces: 0,
      bouncesPerLevel: 0,
      bounceRange: 0,
      lifetime: 2.0,
      spriteUrl: '',
      trailEffect: false,   // BETA 22.70 — flame gout draws its own licking tongues; the dot trail read as circles
      trailColor: '#FF4400',
      projectileShape: 'orb',
      projectileColor: '#FF6600',
      projectileGlowColor: '#FF2200',
      projectileLength: 0,
    },

    maxLevel: 40,
    unlockedByDefault: true,
    unlockQuest: '',
    goopUnlockCost: 0,
    flameCone: true,   // BETA 22.72 — the cone IS the weapon; projectile path skipped
    lockOnRange: 210,  // BETA 22.72 — matches the flame cone reach (190wu + slop) so she engages targets the fire can actually touch
  },

  // ─────────────────────────────────────────────────────────────
  //  VIKING AXE — Viking Peanut's Starter Weapon
  //  Heavy thrown axe. Hits hard and bounces to nearby enemies.
  // ─────────────────────────────────────────────────────────────
  {
    id: 'viking_axe',
    name: 'Viking Axe',
    description: 'Hurled at the nearest enemy. Bounces to nearby targets. SKÅL.',
    owner: 'viking_peanut',
    weaponType: 'projectile',
    fireMode: 'auto_aim',

    spriteUrl: '/weapons/wpn_viking_axe_solid.png',
    spriteSize: 30,
    barrelLength: 32,

    baseDamage: 36,               // BETA 14.73 cycle-9: 28 → 39 (+40%). Throw weapon, bot AI can't aim well; compensate with per-throw damage. Viking median 40 kills was the best non-chad — boost keeps the carry.
    damagePerLevel: 7,
    baseCritChance: 0.12,
    critChancePerLevel: 0.05,
    baseCritDamage: 0.7,
    critDamagePerLevel: 0.12,
    baseKnockback: 8,
    knockbackPerLevel: 0.5,

    baseProjectileCount: 1,
    projectileCountPerLevel: 0,
    burstDelay: 0,
    baseReloadTime: 1.4,
    reloadTimePerLevel: -0.04,
    minReloadTime: 0.55,

    spreadAngle: 4,
    spreadPerLevel: 0,

    projectile: {
      // BETA 20.07 — Dylan: "be thrown 35% faster." 380 → 513 (×1.35).
      // speedPerLevel bumped proportionally (18 → 24) so the boost holds at
      // every weapon level, not just base.
      speed: 513,
      speedPerLevel: 24,
      size: 9,
      sizePerLevel: 0.2,
      piercing: 0,
      piercingPerLevel: 0,
      bounces: 3,
      bouncesPerLevel: 1,         // BETA 15.24 — Dylan: lowest +1
      // BETA 20.46 — Dylan-approved widen. Run data: axe found a bounce target only
      // ~13% of hits at 220 (876 "no target" vs 126 bounces) because enemies are
      // spread out. 220 → 340 (~2.4× scan area) so it chains to the next enemy far
      // more often. Tunable — say the word to go higher/lower.
      bounceRange: 340,
      lifetime: 2.0,
      spriteUrl: '/weapons/wpn_viking_axe_solid.png',
      trailEffect: true,
      trailColor: '#AAAAFF',
      projectileShape: 'orb',
      projectileColor: '#8888DD',
      projectileGlowColor: '#4444BB',
      projectileLength: 0,
      // BETA 20.07 — Dylan: "spin about 20 percent faster." 7 → 8.4 (×1.2).
      spinRate: 8.4,              // Radians/sec — fast axe tumble
      spriteDisplaySize: 36,      // World-units render height
      // BETA 20.07 — Dylan: "remove the vfx glow on them." cleanSprite skips
      // the procedural glow halo + crit halo, leaving just the axe sprite
      // (the blue motion trail stays — say the word if you want that gone too).
      cleanSprite: true,
    },

    maxLevel: 40,
    unlockedByDefault: true,
    unlockQuest: '',
    goopUnlockCost: 0,
    lockOnRange: 350,  // thrown axe with bounces — medium range
    throwable: true,
  },

  // ─────────────────────────────────────────────────────────────
  //  RAGE TOSS — starter weapon
  //  Fires 5 chaotic goop blobs in a wild spread.
  // ─────────────────────────────────────────────────────────────
  {
    id: 'voogoo_rager_toss',
    name: 'Beer Grenade',
    description: 'A dented beer can with a grenade taped on. Explodes on impact in a foam blast and leaves a slippery beer slick.',
    owner: 'mr_silly_peanut',
    weaponType: 'projectile',
    fireMode: 'auto_aim',

    spriteUrl: '/weapons/wpn_beer_grenade_solid.png',
    spriteSize: 22,     // BETA 22.56 — held can (1.81 aspect tight crop; old doll art was portrait)
    barrelLength: 14,   // thrown from the hand, not a barrel

    // BETA 17.88 — Rage Toss Tier-D buff. 50-build LUDICROUS test
    // showed only 72K avg dmg vs Spatula 1.36M (~19× weaker). baseDamage
    // 20 → 30 (+50%) + add piercing so blobs pass through enemies.
    baseDamage: 30,               // BETA 17.88: 20 → 30
    damagePerLevel: 7.5,          // BETA 19.89: 5 → 7.5 — was DPS@L9=109 (1/3 of sniper 273, tier-D). At 7.5/lvl L9 base = 30+60=90 × 5 blobs = 450 burst, ~190 DPS. Mid-tier.
    baseCritChance: 0.06,
    critChancePerLevel: 0.04,
    baseCritDamage: 0.5,
    critDamagePerLevel: 0.08,
    baseKnockback: 2,
    knockbackPerLevel: 0.15,

    baseProjectileCount: 1,        // BETA 21.58 — Dylan: "should throw 1 projectile when you first get the weapon." Was 5 (OP from the jump). +1/level still ramps to a wild spread.
    projectileCountPerLevel: 1,
    burstDelay: 0.02,
    baseReloadTime: 1.1,
    reloadTimePerLevel: -0.03,
    minReloadTime: 0.45,

    spreadAngle: 35,              // DOWN: was 50 — tighter spread means more consistent hits
    spreadPerLevel: -0.5,

    projectile: {
      speed: 500,                 // UP: was 430 — faster blobs catch enemies better
      speedPerLevel: 15,
      size: 5,
      sizePerLevel: 0.2,
      // BETA 22.84 — Dylan-approved: detonate on the FIRST hit like a real
      // grenade (was piercing 5, a 17.88 tier-D buff). Dies + explodes on first
      // enemy contact now (explodeOnImpactRadius 90 fires at that death site).
      piercing: 0,
      piercingPerLevel: 0,
      bounces: 0,
      bouncesPerLevel: 0,
      bounceRange: 0,
      lifetime: 1.3,
      spriteUrl: '/weapons/wpn_beer_grenade_solid.png',
      trailEffect: true,
      trailColor: '#00FF88',
      projectileShape: 'orb',
      projectileColor: '#00DD66',
      projectileGlowColor: '#00FF44',
      projectileLength: 0,
      spinRate: 6,                // the can tumbles end-over-end
      // BETA 22.56 — Dylan: "Beer grenade explodes on impact." Foam blast
      // hits everything near the impact at full projectile damage, then the
      // spilled beer lingers as a slippery slow slick.
      explodeOnImpactRadius: 90,
      explodeOnImpactDamageMult: 1.0,
      aoeOnImpactRadius: 70,
      aoeOnImpactDuration: 3.0,
      aoeOnImpactSlowMult: 0.65,
      aoeOnImpactDotPerSec: 0,
      aoeOnImpactColor: 'rgba(240,222,170,0.42)',
      spriteDisplaySize: 26,
    },

    maxLevel: 40,
    unlockedByDefault: true,
    unlockQuest: '',
    goopUnlockCost: 0,
    lockOnRange: 220,  // chaotic spread — enemies need to be close to get hit by blobs
    throwable: true,
  },

  // ─────────────────────────────────────────────────────────────
  //  TACTICAL BASKETBALL — LeBron Peanut's Starter Weapon
  //  A bouncy orb that chains between multiple enemies.
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tactical_basketball',
    name: 'Dodge This',
    description: 'Skull-stamped dodgeball. Hits the next target. Then the next. Dodge this.',
    owner: 'lebron_peanut',
    weaponType: 'projectile',
    fireMode: 'auto_aim',

    spriteUrl: '/weapons/wpn_dodgeball_solid.png',
    spriteSize: 32,
    barrelLength: 30,

    // BETA 16.91 — PSBOT cycle-30k §11B + Dylan "yes": Tactical
    // Basketball was the OVER-tuned outlier (4775 avg/hit on Killa,
    // 5x higher than next-strongest weapon). The bounce-chain
    // 1.25× compounding (LeBron's King's Court signature) feeds
    // each bounce damage on top of the base, so small base bumps
    // cascade. Pulling baseDamage 30 → 22 (revert prior buff) and
    // damagePerLevel 8 → 5. Should land basketball in the 1500-2500
    // avg/hit band post-change instead of 4775.
    // BETA 19.50 — Dylan live: "basketball should take 2 hits to kill a zombie
    // for base damage." With ENEMY_HP_GLOBAL_MULT rolled back 2.0→1.0, zombie
    // is 50 HP at run start; basketball at 22 dmg = 3 hits. Bump to 25 →
    // exactly 2-shot. Bounce-chain 1.25× still compounds for King's Court so
    // late-game DPS stays the basketball-flavored outlier Dylan wanted.
    baseDamage: 25,
    // BETA 19.89 — full balance audit: basketball DPS@L9 = 100, sniper 273.
    // Basketball is supposed to be Dylan's signature bounce weapon but lands
    // at exactly median DPS. Bump damagePerLevel 5 → 8 so L9 lands at ~145
    // base damage × bounce chain (1.25× per of 5 base bounces = ×3.05 final)
    // = real DPS climbs to ~300+ on packed crowds. Single-target stays low
    // (matches design intent — basketball is a crowd-clear, not single-target).
    damagePerLevel: 8,
    baseCritChance: 0.08,
    critChancePerLevel: 0.05,
    baseCritDamage: 0.6,
    critDamagePerLevel: 0.10,
    // BETA 19.73 — Dylan live: "knockback should be 20" for basketball.
    // Was: 5 (light shove). Now: 20 (4× — significant shove, chains feel
    // physically meaningful on each bounce as the ball drives enemies
    // back). Per-level scaling unchanged so late-game knockback scaling
    // stays the same character/build curve.
    baseKnockback: 20,
    knockbackPerLevel: 0.3,

    baseProjectileCount: 1,
    projectileCountPerLevel: 0,
    burstDelay: 0,
    // BETA 18.19 — Early-game LeBron buff. Live-run data: LeBron died at
    // 27.6s with 1 kill (zero levels) — basketball couldn't ramp fast
    // enough to keep up with zombie pressure. Reload 1.3 → 1.0 gives ~30%
    // more shots in the first 30 seconds without touching per-hit damage
    // (which was the source of the 16.91 overtune). Per-level reload
    // scaling unchanged so the late-game ceiling is the same.
    baseReloadTime: 1.0,
    reloadTimePerLevel: -0.04,
    minReloadTime: 0.5,

    spreadAngle: 0,
    spreadPerLevel: 0,

    projectile: {
      // BETA 19.74 — Dylan live: "the basketball is moving to slow to hit
      // its target." Ball was outrun by zombies at 420 — chain would expire
      // (lifetime 2.2s) before reaching the next target. Doubled to 840 so
      // the ball actually catches enemies and the bounce chain completes.
      // Per-level scaling unchanged so late-game curve shape is the same.
      speed: 840,
      speedPerLevel: 20,
      size: 10,
      sizePerLevel: 0.25,
      piercing: 0,
      piercingPerLevel: 0,
      bounces: 5,                 // UP: was 4 — one extra base bounce for better pack clearing
      bouncesPerLevel: 1,         // BETA 15.24 — Dylan: lowest +1
      bounceRange: 260,
      lifetime: 2.2,
      spriteUrl: '/weapons/wpn_dodgeball_solid.png',
      trailEffect: true,
      // BETA 20.72 — GOLD glow identity. trail/glow were literally Fireball's
      // deep-orange palette (#FF8800/#FF4400) — two weapons, one look. Ball
      // now owns GOLD (crit halo + fallback orb + the new gold bounce bursts);
      // Fireball keeps deep orange. Body stays ball-orange for the fallback.
      trailColor: '#FFD700',
      projectileShape: 'orb',
      projectileColor: '#FF6600',
      projectileGlowColor: '#FFD700',
      projectileLength: 0,
      spinRate: 5,                // Moderate backspin like a real pass
      spriteDisplaySize: 32,      // World-units render height
    },

    maxLevel: 40,
    unlockedByDefault: true,
    unlockQuest: '',
    goopUnlockCost: 0,
    lockOnRange: 360,  // bouncing ball — medium range, chains to nearby enemies
    throwable: true,
  },

  // ─────────────────────────────────────────────────────────────
  //  THE CHAD AURA — Chad Peanut's Starter Weapon
  //  A golden ring surrounds the player. Enemies that touch it
  //  take damage on each pulse. No projectiles — you ARE the weapon.
  // ─────────────────────────────────────────────────────────────
  {
    id: 'chad_aura',
    name: 'Personal Space',
    description: 'A golden ring of pure chad energy. Enemies that touch it get cooked on every pulse. You don\'t aim. You exist.',
    owner: 'chad_peanut',
    weaponType: 'aoe',
    fireMode: 'auto_aim',
    aura: true,

    spriteUrl: '/weapons/weapon_09_chad_aura.png',  // still shown in HUD icon grid
    spriteSize: 30,
    barrelLength: 0,  // no barrel — not a projectile weapon

    // BETA 17.88 — CHAD AURA OVERHAUL. Dylan: "fix how chad aura scales."
    // 50-build LUDICROUS telemetry: chad_aura averaged 3,243 dmg/build
    // (~400× weaker than Spatula's 1.36M). Root cause: piercing: 0 →
    // aura ticks hit ONE enemy at a time, no matter how many are in
    // the ring. Spatula+Vampire Fang dominate via piercing: 99. Fix
    // brings Chad Aura to "horde-clear" identity per its design intent.
    //
    // Changes (NO caps per Dylan, fix scaling):
    //   piercing: 0 → 99            (aura ticks hit ALL enemies in ring)
    //   damagePerLevel: 3.5 → 5.5   (faster per-level scaling)
    //   reloadTimePerLevel: -0.01 → -0.015  (faster cooldown ramp)
    //   minReloadTime: 0.10 → 0.08  (higher peak fire rate)
    //   sizePerLevel: 5 → 7          (ring grows slightly faster)
    baseDamage: 30,
    damagePerLevel: 5.5,           // BETA 17.88: 3.5 → 5.5
    baseCritChance: 0.08,
    critChancePerLevel: 0.03,
    baseCritDamage: 0.5,
    critDamagePerLevel: 0.06,
    baseKnockback: 2,
    knockbackPerLevel: 0.2,

    baseProjectileCount: 1,
    projectileCountPerLevel: 0,
    burstDelay: 0,
    // BETA 21.38 — Dylan: too fast off the rip. 0.30 (~3.3/sec) → 0.50 (~2/sec start);
    // ramp unchanged so it climbs back to the 0.12 floor (~8/sec) by ~Lv26.
    // BETA 21.40 — steeper ramp so the aura reaches full tick rate by ~Lv8;
    // 2.2/s @Lv1 → 8.3/s @Lv8. Slow level-1 start preserved.
    baseReloadTime: 0.45,
    reloadTimePerLevel: -0.055,
    // BETA 19.89 — full balance audit: chad_aura @ L9 hits 275 DPS = 3× median
    // weapon DPS (~100). 17.88 fix (pierce 99 + faster ramp) over-corrected.
    // Floor 0.08 → 0.12 drops peak DPS to ~183. Still horde-clear identity,
    // no longer 3× other weapons. Aura still pierces all enemies in ring.
    minReloadTime: 0.12,

    spreadAngle: 0,
    spreadPerLevel: 0,

    projectile: {
      // projectile.size is repurposed as RING RADIUS in world units.
      // sizePerLevel makes the ring grow with each level — meaningful upgrade.
      speed: 0,
      speedPerLevel: 0,
      // BETA 17.19 — PSBOT cycle-30ah: bumped radius 99 → 130 for survival.
      // BETA 17.88 — Dylan: "fix how chad aura scales." piercing 0 → 99 so
      // aura ticks hit ALL enemies inside the ring (matches Spatula/Fang's
      // piercing model — the core thing that gave them 400× more dmg). Also
      // sizePerLevel 5 → 7 so radius grows faster with weapon level. Same
      // VS pattern: "auras need to dominate the area they cover."
      size: 130,
      sizePerLevel: 7,           // BETA 17.88: 5 → 7
      piercing: 99,              // BETA 17.88: 0 → 99 (aura hits ALL enemies in ring)
      piercingPerLevel: 0,
      bounces: 0,
      bouncesPerLevel: 0,
      bounceRange: 0,
      lifetime: 0,
      spriteUrl: '',
      trailEffect: false,
      trailColor: '#FFD700',
      projectileShape: 'orb',
      projectileColor: '#FFD700',
      projectileGlowColor: '#FFB800',
      projectileLength: 0,
    },

    maxLevel: 40,
    unlockedByDefault: true,
    unlockQuest: '',
    goopUnlockCost: 0,
    lockOnRange: 55,  // matches base ring radius — doesn't target, just for reference
  },

  // ─────────────────────────────────────────────────────────────
  //  THE BLUNDERBUSS — Pirate Peanut's Starter Weapon
  //  Shotgun. 8 pellets, brutal up close, useless at range.
  // ─────────────────────────────────────────────────────────────
  {
    id: 'blunderbuss',
    name: 'Sawed-Off',
    description: 'Unloads 8 pellets in a wide cone. Get close. Very close.',
    owner: 'pirate_peanut',
    weaponType: 'projectile',
    fireMode: 'auto_aim',

    spriteUrl: '/weapons/wpn_sawed_off_solid.png',
    spriteSize: 34,
    // BETA 14.10 — calibrator: dx 27.91, dy -3.4
    barrelLength: 27.91,
    barrelOffsetY: -3.4,

    // BETA 16.48 — PSBOT cycle-30j REVISED per Dylan: "the pirate gun
    // should kill a zombie in one hit at close range at the start of
    // game. so whatever that damage needs to be fix it." 16.44 shipped
    // baseDamage 22 / spread 42 — still didn't one-shot a 50-HP zombie
    // at lvl 1 close range. PSBOT reverse-engineered cone geometry
    // (zombie hitbox ~20wu wide; 35° spread at 50wu lands ~5 of 8
    // pellets vs 42° landing 4 of 8 vs 52° original landing 3-4).
    // New combined buff makes lvl 1 close range a guaranteed one-shot:
    //   baseDamage 14 → 25 (+79% from original; +14% over 16.44)
    //   damagePerLevel 2.5 → 3.5 (40% scaling, kept from 16.44)
    //   projectileCountPerLevel 0 → 0.25 (kept)
    //   baseReloadTime 1.7 → 1.4 (kept)
    //   minReloadTime 0.65 → 0.55 (kept)
    //   spreadAngle 52 → 35 (TIGHTER — was 42 in 16.44, now 35 to land
    //     more pellets on the zombie hitbox at close range. Iconic
    //     shotgun feel preserved: devastating up close, weak past 200wu)
    // Verification at lvl 1, 50wu range, 35° spread:
    //   cone width 31wu, ~5 pellets land × 25 = 125 dmg → zombie (50 HP) dies
    //   At 30wu point-blank: all 8 pellets × 25 = 200 dmg
    //   At 400wu far: 0.6 pellets avg = ~15 dmg (range falloff intact)
    // BETA 17.88 — Blunderbuss Tier-D buff. LUDICROUS test: 68K dmg/build
    // vs Spatula 1.36M (~20× weaker). Bump pellet damage + give piercing
    // so close-range pellets cut through multiple enemies in a horde.
    baseDamage: 35,                 // BETA 17.88: 25 → 35
    damagePerLevel: 7,              // BETA 19.89: 5 → 7 — was DPS@L9=100 (1/3 of median 273 sniper). At 7/lvl L9 base = 35+56=91 × 8 pellets = 728 burst per shot. Closer to mid-tier.
    baseCritChance: 0.06,
    critChancePerLevel: 0.04,
    baseCritDamage: 0.4,
    critDamagePerLevel: 0.08,
    baseKnockback: 4,
    knockbackPerLevel: 0.25,

    baseProjectileCount: 8,
    projectileCountPerLevel: 0.5,   // BETA 17.88: 0.25 → 0.5 (lvl 40 = +20 pellets)
    burstDelay: 0,
    baseReloadTime: 1.4,
    reloadTimePerLevel: -0.05,
    minReloadTime: 0.55,

    spreadAngle: 35,
    spreadPerLevel: -0.6,

    projectile: {
      speed: 1400,                  // BETA 15.1: 360 → 1400 (sniper baseline). Blunderbuss pellets fly fast — short range still gates use.
      speedPerLevel: 12,
      size: 4,
      sizePerLevel: 0.1,
      piercing: 0,
      piercingPerLevel: 0,
      bounces: 0,
      bouncesPerLevel: 0,
      bounceRange: 0,
      lifetime: 1.0,             // UP: was 0.55 — pellets now travel ~360px instead of 198px; still a shotgun, just usable past point-blank
      spriteUrl: '',
      trailEffect: false,
      trailColor: '#AAAAAA',
      // BETA 22.76 — hot lead shot-pellets. The bespoke pellet body (dark rim +
      // offset gold hotspot) is drawn by WEAPON_VFX['blunderbuss'].drawBody in
      // weaponVfx.ts; projectileShape 'circle' has NO dedicated render branch
      // (it falls to the generic default), so the drawBody hook is what gives
      // the pellet its identity. shape/colors are RENDER-ONLY (collision uses
      // size above). (22.75 audit: the old comment claimed a 'round-pellet
      // branch with a white center' that never existed — pellets rendered as
      // generic RPK-style circles.)
      projectileShape: 'circle',
      projectileColor: '#FFD700',
      projectileGlowColor: '#B8860B',
      projectileLength: 0,
    },

    maxLevel: 40,
    unlockedByDefault: true,
    unlockQuest: '',
    goopUnlockCost: 0,
    lockOnRange: 220,  // BETA 15.30: 170 → 220 per Dylan — pirate dying before reaching enemies. Still shortest in the game (next-up: viking/wizard at 280-320).
  },

  // ═══════════════════════════════════════════════════════════════
  //  BETA 13.94 — NEW WEAPONS (Phase 1: basic stats, no special mechanics yet)
  //  Sprites bundled in /public/weapons/. Mechanics shipped as basic
  //  projectile/arc/aura. Phase 2 will add: Ugly Kittens freeze + 3-frame
  //  cycle, Karen Drones orbital companion, Terminator boomerang return,
  //  Vampire Fang lifesteal, Squirter WET / Frozen Darts FREEZE / Hurray
  //  disorient status effects, Poop Sling slowing puddle, Shitty Cannon
  //  AoE smoke cloud, Ex Girlfriend 5-shot spread.
  // ═══════════════════════════════════════════════════════════════

  // ─── Ultra Beam — single charged piercing beam (BETA 15.74 REWORK) ────
  // BETA 15.74 — FULL REWORK per PSBOT RUN spec + Dylan's new audio file.
  // Old: 80-segment continuous-beam with sustained-damage feel.
  // New: SINGLE epic charged shot. Audio file has 0.737s charge-up build,
  // then BLAM at t=0.737s — projectile spawns at the BLAM moment.
  // Aim direction LOCKED at trigger, full-map travel, pierces all enemies,
  // stops at walls, baseDamage tuned to 1-shot a base zombie (50 HP).
  // Bonus projectile fan: alternating right/left at 1.5° per step (in
  // weaponSystem.ts fireProjectile bonus loop, ultra-beam-specific path).
  // Audio playbackRate scales with cooldown so the BLAM stays aligned at
  // any attack speed (see weaponAdapter ultra-beam fire path).
  {
    id: 'wpn_ultra_beam',
    name: 'Ultra Beam',
    description: 'Charged epic piercing beam. 0.737s charge-up, full-map travel, pierces all.',
    owner: '',
    weaponType: 'projectile',
    fireMode: 'auto_aim',
    spriteUrl: '/weapons/wpn_ultra_beam_solid.png',
    spriteSize: 36,
    barrelLength: 27,
    barrelOffsetY: -2,
    spriteRotationOffset: 0,
    spriteScale: 1.3,
    // BETA 15.74 — One-shot model. baseDamage 60 reliably one-shots a base
    // zombie (50 HP) per PSBOT spec. Damage scales with player.damageBonus.
    // BETA 17.10 — PSBOT cycle-30z + cycle-30aa: 445 avg damage per run
    // (extremely low). Root cause is the 0.737s charge-up + ~1 shot/sec
    // rate, so weapon barely fires across a 60-72s run. Damage per shot
    // is fine (1-shots zombies) but late-game scaling is anemic. Bumped
    // damagePerLevel 4 → 8 so by lvl 5-7 Ultra Beam meaningfully cuts
    // elite/boss HP per shot, justifying the slow rate. baseDamage
    // unchanged (1-shot model preserved at low levels).
    baseDamage: 37,
    damagePerLevel: 8,
    baseCritChance: 0.08,
    critChancePerLevel: 0.02,
    baseCritDamage: 0.5,
    critDamagePerLevel: 0.05,
    baseKnockback: 0,
    knockbackPerLevel: 0,
    // BETA 15.74 — SINGLE projectile per fire (was 80 segments). burstDelay 0.
    // The bonus-projectile fan in weaponSystem.ts's fireProjectile path adds
    // additional beams when player has bonusProjectileCount > 0.
    baseProjectileCount: 1,
    projectileCountPerLevel: 0,
    burstDelay: 0,
    // BETA 15.74 — Cooldown 0.737s base = matches the audio file's 0.737s
    // charge-up duration. At 1× attack speed, audio plays at 1× and BLAM
    // lands exactly when the next-fire eligibility ends. Faster attack
    // speed scales both audio + chargeUpMs proportionally (handled in
    // weaponAdapter). Hard floor 0.3s = 246% atk speed cap.
    baseReloadTime: 0.737,
    reloadTimePerLevel: 0,
    minReloadTime: 0.3,
    // BETA 15.74 — 0.737s charge-up to align audio BLAM with projectile spawn.
    // Aim direction LOCKED at trigger (player can move during charge but the
    // beam goes wherever they were aimed when they pulled the trigger).
    chargeUpMs: 737,
    spreadAngle: 0,
    spreadPerLevel: 0,
    projectile: {
      // BETA 15.74 — VERY HIGH speed = effectively instant traversal across
      // the map. lifetime 1s ensures it can clear even a worst-case room
      // chain. piercing 999 = goes through everything. The beam visual
      // (projectileShape:'beam') still renders as a long line.
      speed: 8000, speedPerLevel: 0,
      size: 8, sizePerLevel: 0,
      piercing: 999, piercingPerLevel: 0,
      bounces: 0, bouncesPerLevel: 0, bounceRange: 0,
      lifetime: 1.0, spriteUrl: '',
      trailEffect: true, trailColor: '#bb55ff',
      projectileShape: 'beam', projectileColor: '#bb55ff', projectileGlowColor: '#9933ff',
      projectileLength: 80, spinRate: 0,
      cleanSprite: false,
    },
    maxLevel: 40,
    unlockedByDefault: false,
    unlockQuest: '',
    goopUnlockCost: 0,
    lockOnRange: 9999,
    // BETA 13.97 — strips 8 shield + 2 armor per HIT. Pierces, so a single shot
    // can strip multiple enemies of their protective layers in one beam.
    shieldStripPerHit: 8,
    armorStripPerHit: 2,
  },

  // ─── The Terminator — boomerang ───────────────────────────────
  {
    id: 'wpn_the_terminator',
    name: 'Toilet Seat Boomerang',
    description: 'Half a toilet seat, all boomerang. Returns to you and hits everything AGAIN on the way back.',
    owner: '',
    weaponType: 'projectile',
    fireMode: 'auto_aim',
    spriteUrl: '/weapons/wpn_toilet_boomerang_solid.png',
    spriteSize: 36,
    barrelLength: 22,
    baseDamage: 28,
    damagePerLevel: 4,
    baseCritChance: 0.10,
    critChancePerLevel: 0.03,
    baseCritDamage: 0.6,
    critDamagePerLevel: 0.05,
    baseKnockback: 4,
    knockbackPerLevel: 0.3,
    baseProjectileCount: 1,
    projectileCountPerLevel: 0,
    burstDelay: 0,
    baseReloadTime: 1.4,
    reloadTimePerLevel: -0.06,
    minReloadTime: 0.6,
    spreadAngle: 0,
    spreadPerLevel: 0,
    projectile: {
      speed: 700, speedPerLevel: 5,    // BETA 15.8: 1400 → 700 per Dylan ("terminator boomerang is too fast. give it half the speed"). speedPerLevel halved too.
      size: 16, sizePerLevel: 0.5,
      piercing: 1, piercingPerLevel: 0,                  // BETA 15.42: 4 → 1 per Dylan spec correction via PSBOT RUN — "boomerang terminator is supposed to bounce on enemies. base is 1 bounces so can hit 2 enemies with that bounce, and then come back." Pierce-4 was overshoot (let shot tunnel through 4 in a line BEFORE bouncing). Pierce-1 = projectile stops at first enemy unless it bounces.
      bounces: 1, bouncesPerLevel: 0, bounceRange: 250,  // BETA 15.42: 2 → 1 per Dylan spec correction. 15.8 set this to 2 ("plus 2 bounce for base levels"); that was wrong, intended is 1 bounce after first hit = 2 enemies on outbound. bounceRange 250 stays so the single bounce chains to a nearby enemy.
      // BETA 14.08 — lifetime extended (2→6s) so the boomerang has time to
      // outbound-fly + come back even if player kites. Returning-phase
      // projectiles are now exempt from lifetime expiry anyway (see
      // weaponSystem.ts:1292) so they truly stay alive until they reach
      // the player. User: "should boomerang and not be done attacking
      // until it gets back to main character/bot".
      lifetime: 6.0, spriteUrl: '/weapons/wpn_toilet_boomerang_solid.png',
      // BETA 14.08 — trail OFF per user "remove white vfx on the terminator".
      // BETA 20.72 — trail back ON but NOT the 14.08 white dots: outbound leg
      // gets sprite ghost afterimages, return leg gets a pure CYAN streak
      // (weaponSystem return-streak branch) so you can read out vs back at a
      // glance — and tell it from Vampire Fang's blood-red return at distance.
      trailEffect: true, trailColor: '#00E5FF',
      projectileShape: 'circle', projectileColor: '', projectileGlowColor: '',
      projectileLength: 0, spinRate: 22,
      boomerangReturn: true,
      spriteDisplaySize: 36,
      cleanSprite: true,
    },
    maxLevel: 40,
    unlockedByDefault: false,
    unlockQuest: '',
    goopUnlockCost: 0,
    lockOnRange: 280,
    throwable: true,
  },

  // ─── Ugly Kittens — Monkey-Bomb-style lure trap (BETA 16.69) ─────
  // Per Dylan: "we need to upgrade the kittens that enemies are drawn do
  // it like monkeys in call of duty zombies. there should be a max of
  // how many zombies one kitten placement wil attract. and then upgrades
  // for the kittens will be a larger ammount and suck. and larger
  // attaack radious. so the kittens are a much beter weapon to chose."
  //
  // BEHAVIOR:
  //   1. Player places kitten on ground (auto-aim drop).
  //   2. Kitten ACTIVELY LURES up to N enemies within R wu — they steer
  //      toward the kitten instead of the player.
  //   3. Each enemy that contacts the kitten gets damage + 3s freeze.
  //      Kitten persists (piercing 999) so it can hit multiple enemies
  //      sequentially during its 12-second lifetime.
  //   4. Lifetime expiry → kitten despawns naturally.
  //   5. Upgrades scale lure radius + max-lured count + damage.
  {
    id: 'wpn_ugly_kittens',
    name: 'Cat Cannon',
    description: 'A cannon that fires possessed kittens. They CHASE the nearest enemy at YOUR run speed and claw to KILL. Each cat has ~6s to score a kill, then it vanishes — base 1 kill per cat (raise it with pierce to kill more). Level up = more cats at once.',
    owner: '',
    weaponType: 'projectile',
    fireMode: 'auto_aim',
    spriteUrl: '/weapons/wpn_cat_cannon_solid.png',
    spriteSize: 30,     // BETA 22.56 — the launcher tube (kittens stay the projectile)
    barrelLength: 21,   // cats leave the muzzle
    baseDamage: 36,                // BETA 15.10: 18 → 51 per Dylan ("ugly kittens should be at base able to kill a zombie at with one touch"). 1-shot 50-HP zombie.
    damagePerLevel: 5,
    baseCritChance: 0.05,
    critChancePerLevel: 0.02,
    baseCritDamage: 0.5,
    critDamagePerLevel: 0.05,
    baseKnockback: 0,
    knockbackPerLevel: 0,
    baseProjectileCount: 1,
    projectileCountPerLevel: 0.2,  // BETA 20.63 — Dylan: still too many. 0.5 → 0.2 (chasing pets spread out; ~3 cats by Lv10, ~9 by Lv40).
    burstDelay: 0,
    baseReloadTime: 1.3,           // BETA 20.52 — Dylan: "placed too quickly". 1.0 → 1.3.
    reloadTimePerLevel: -0.02,
    minReloadTime: 0.55,           // BETA 20.52 — 0.4 → 0.55 (cap the fastest placement rate).
    spreadAngle: 0,
    spreadPerLevel: 0,
    projectile: {
      // BETA 20.63 — CHASING PET (was speed 0 / stationary lure-trap). The cat
      // runs at the player's live move speed toward the nearest enemy; the
      // homing + speed-sync happen each frame in weaponSystem (this 200 is just
      // a non-zero seed so it's not treated as a trap). BETA 21.70 — piercing 0
      // (single-target base); the per-cat CLAW BUDGET (weaponSystem kittenClawBudget,
      // = base pierce + global pierce buffs + 1) + the 1s hitEnemyIds re-claw clear
      // let one pet bite its budget of enemies per second as it runs the swarm. 9s lifetime.
      speed: 200, speedPerLevel: 0,
      size: 18, sizePerLevel: 0.4,
      // BETA 21.70 — Dylan: "kitten attacks many enemies early game — should only
      // attack 1 enemy at the start. there should be buffs we can get for the kitten
      // to attack more than one." 6 → 0 = SINGLE-TARGET base. The cat now bites ONE
      // enemy per re-claw cycle; pierce buffs (N08 Not Ritalin, pierce shrines) raise
      // the "claw budget" so it claws through more. The budget machinery lives in
      // weaponSystem (kittenClawBudget / kittenClawLeft) so the pet still persists 9s.
      piercing: 0, piercingPerLevel: 0,
      bounces: 0, bouncesPerLevel: 0, bounceRange: 0,
      lifetime: 6, spriteUrl: '/weapons/wpn_ugly_kittens.png',  // BETA 22.04 — 9 → 6 (Dylan: a cat gets ~6s to score a kill, then it's gone — no more clawing the whole crowd)
      trailEffect: false, trailColor: '',
      projectileShape: 'circle', projectileColor: '', projectileGlowColor: '',
      projectileLength: 0, spinRate: 0,
      cleanSprite: true,
      spriteDisplaySize: 28,
    },
    maxLevel: 40,
    unlockedByDefault: false,
    unlockQuest: '',
    goopUnlockCost: 0,
    lockOnRange: 240,
    // BETA 21.35 — Dylan: "see if cats need nerfed." Removed their freeze entirely —
    // cats are a DAMAGE/chase weapon now, not crowd-control. Frozen Darts is the
    // dedicated slow weapon. (Stops cats + darts double-stacking CC on the whole swarm.)
    projectileAnimFrames: [
      '/weapons/wpn_ugly_kittens_atk2.png',
      '/weapons/wpn_ugly_kittens_atk3.png',
      '/weapons/wpn_ugly_kittens_atk4.png',
    ],
    projectileAnimFrameDuration: 0.12,
    // BETA 16.69 — Lure stats (Monkey-Bomb suck-radius). Scales with weapon level.
    baseLureRadius: 0,             // BETA 20.63 — lure DISABLED. Cats CHASE enemies now instead of sucking them in, so the monkey-bomb lure is retired for kittens.
    lureRadiusPerLevel: 0,
    baseMaxLured: 0,
    maxLuredPerLevel: 0,
  },

  // ─── Karen Drones (SWARM REDESIGN) — orbital purse-throwing Karens ─────
  // BETA 17.11 — PSBOT cycle-30ac full redesign after 5 cycles of failed
  // contact-hit fixes. Per Dylan: "make karen drones throw this purse as a
  // projectile. has bounce if bounce is added." Switched from broken
  // orbitalContactDamage path (1.2-5.5% hit rate across 6 audits) to the
  // proven projectile path (same as RPK/squirter at 60-95% hit rate). The
  // drones still visually orbit the player; one of them throws a Louis-
  // Vuitton purse projectile at the nearest enemy each fire cycle. Purse
  // bounces if player has projectileBounceBonus.
  {
    id: 'wpn_karen_drones',
    name: 'Karen Drones',
    description: 'Designer-purse-swinging Karens orbit you, hurling Louis bags at the help. Bounces if you stack bounce.',
    owner: '',
    weaponType: 'projectile',
    fireMode: 'auto_aim',
    spriteUrl: '/weapons/wpn_karen_drones.png',
    spriteSize: 56,
    barrelLength: 0,
    // BETA 17.11 — buffed for projectile path. Was 17 (contact hit assumed
    // 4Hz tick density). New path fires every reloadTime so per-shot damage
    // needs to be higher to maintain DPS parity.
    // BETA 20.72 — Dylan-approved (123-run audit): Karen Drones were the
    // WEAKEST weapon in the roster at 0.30× median dmg/min (parity floor
    // ~0.5×). 24 → 34; purse speed bump below clears the 70% hit-rate law.
    baseDamage: 34,
    damagePerLevel: 4,
    baseCritChance: 0.10,
    critChancePerLevel: 0.02,
    baseCritDamage: 0.5,
    critDamagePerLevel: 0.05,
    baseKnockback: 6,
    knockbackPerLevel: 0.4,
    baseProjectileCount: 1,
    projectileCountPerLevel: 0,
    burstDelay: 0,
    baseReloadTime: 1.0,
    reloadTimePerLevel: -0.03,
    minReloadTime: 0.4,
    spreadAngle: 0,
    spreadPerLevel: 0,
    projectile: {
      // BETA 20.72 — 460 → 600 (audit: purse slower than fleeing enemies at
      // its 360wu lock-on range = chronic near-misses, 68.3% hit rate).
      speed: 600, speedPerLevel: 12,
      size: 16, sizePerLevel: 0.5,
      piercing: 0, piercingPerLevel: 0,
      // BETA 17.11 — base 0 bounces but adds via player.projectileBounceBonus.
      // Bounce range 220wu so purse can chain through enemy clumps.
      bounces: 0, bouncesPerLevel: 0, bounceRange: 220,
      lifetime: 1.4,
      spriteUrl: '/weapons/karen_purse_projectile.png',
      // BETA 20.38 — Dylan: "purse sprite too small, needs 2.5x." Auto size was
      // renderSize×0.75 = ~12 world-units; 30 = 2.5× that. SPRITE-ONLY (hitbox
      // stays size:16, so no gameplay change), in line with other throwables (26-40).
      spriteDisplaySize: 30,
      // BETA 20.34 — Dylan: "zero vfx on her projectile, just use the purse sprite."
      // cleanSprite kills the yellow faux-glow ring; trailEffect off kills the yellow trail.
      cleanSprite: true,
      trailEffect: false, trailColor: '#FFD700',
      // BETA 17.11 — 'circle' shape but sprite override means the engine
      // renders the purse PNG instead of the colored circle. Color values
      // are fallback if sprite fails to load.
      // BETA 20.72 — fallback recolored: gold core + purple "designer" glow
      // (was brown-on-gold mud). Only visible on sprite-load failure — the
      // flying purse itself stays 100% clean per Dylan 20.34.
      projectileShape: 'circle', projectileColor: '#FFD700', projectileGlowColor: '#AA44FF',
      projectileLength: 0,
    },
    maxLevel: 40,
    unlockedByDefault: false,
    unlockQuest: '',
    goopUnlockCost: 0,
    lockOnRange: 360,
    // BETA 13.96 — Karen orbits the player. BETA 17.11 — orbital is now
    // purely visual identity; firing uses proven projectile path.
    orbital: true,
    orbitRadius: 95,
    orbitSpeed: 1.4,
    orbitalPropellers: true,
    // orbitalContactDamage REMOVED in BETA 17.11 — the contact-hit path was
    // broken across 6 cycles AS A PRIMARY attack. Projectile path is the
    // proven approach for primary damage.
    // BETA 20.06 — SECONDARY contact damage re-added per Dylan: the spinning
    // drones now chip enemies they touch (0.5× weapon damage per touch, 0.4s
    // per-enemy cooldown) WHILE still throwing the purse projectile as primary.
    // The 6-cycle hit-rate problem doesn't matter here because the projectile
    // carries the DPS; this is bonus flavor damage for the satisfying spin.
    orbitalSecondaryContactDamage: 0.5,
    orbitalContactRadius: 40,
    orbitalContactCooldown: 0.4,
  },

  // ─── Poop Sling — aimed slow projectile ───────────────────────
  {
    id: 'wpn_poop_sling',
    name: 'The Thumper',
    description: 'Beat-up M79 thumper lobbing stink shells. Heavy AoE splat leaves a slowing puddle.',
    owner: '',
    weaponType: 'projectile',
    fireMode: 'auto_aim',
    spriteUrl: '/weapons/wpn_grenade_launcher.png',
    spriteSize: 22,     // BETA 22.55 — M79 is a long thin tube (3.08 aspect crop), was a tall sling
    barrelLength: 32,   // BETA 22.55 — muzzle at the far end of the tube
    // BETA 17.88 — Poop Sling Tier-D buff. LUDICROUS test: 43K dmg/build
    // (worst non-Chad-Aura). Bump baseDamage + add piercing so the heavy
    // poop ball passes through enemy lines, plus keep the AoE puddle.
    baseDamage: 37,                 // BETA 17.88: 42 → 60
    damagePerLevel: 8,              // BETA 17.88: 6 → 8
    baseCritChance: 0.08,
    critChancePerLevel: 0.02,
    baseCritDamage: 0.6,
    critDamagePerLevel: 0.08,
    baseKnockback: 5,
    knockbackPerLevel: 0.4,
    baseProjectileCount: 1,
    projectileCountPerLevel: 0,
    burstDelay: 0,
    baseReloadTime: 1.3,
    reloadTimePerLevel: -0.05,
    minReloadTime: 0.6,
    spreadAngle: 0,
    spreadPerLevel: 0,
    projectile: {
      speed: 280, speedPerLevel: 8,
      size: 14, sizePerLevel: 0.6,
      // BETA 17.88 — piercing 0 → 8 so the heavy poop ball passes through line of enemies
      piercing: 8, piercingPerLevel: 0.5,
      bounces: 0, bouncesPerLevel: 0, bounceRange: 0,
      lifetime: 1.5, spriteUrl: '/weapons/wpn_poop_sling_ammo.png',
      // BETA 13.101 — TRAIL OFF. The mid-flight brown trail was rendering ON
      // TOP of the player (because trails ride with the projectile in render
      // pipeline). User: "poop sling animation is over the top my character
      // and not under". Impact puddle (aoeOnImpact*) is a ground effect and
      // already renders UNDER characters via renderGroundEffects.
      // BETA 20.72 — trail back ON via the 20.69 sprite-GHOST path (faint
      // spinning poop afterimages @0.35 alpha), NOT the 13.101 flat brown
      // discs that buried the player. Light lob vs Shitty Cannon's heavy 0.55.
      trailEffect: true, trailColor: '',
      // Empty colors — sprite IS the projectile, no orb-glow fallback.
      projectileShape: 'circle', projectileColor: '', projectileGlowColor: '',
      projectileLength: 0, spinRate: 10,
      // BETA 22.84 — Dylan-approved: The Thumper is a GRENADE launcher — it
      // detonates in a real AoE blast on impact now (was slow-puddle only).
      // Fires via spawnImpactExplosion at the death site; the brown puddle
      // below stays as the aftermath.
      explodeOnImpactRadius: 60,
      explodeOnImpactDamageMult: 0.55,
      // BETA 13.97 — slowing brown puddle at impact: 60wu radius, 4s, 50% slow, no DoT
      aoeOnImpactRadius: 60,
      aoeOnImpactDuration: 4.0,
      aoeOnImpactSlowMult: 0.5,
      aoeOnImpactDotPerSec: 0,
      aoeOnImpactColor: 'rgba(110,75,40,0.55)',
      // BETA 13.101 — use the canonical SHIT/poop floor sprite for the puddle.
      aoeOnImpactSpriteUrl: '/items/item_poop_deck_ground.png',
      // BETA 14.60 — stage-2 sprite swap on first enemy contact. Reuses
      // the existing item_shit stage-2 sprite for consistent visual language
      // across all poop content (poop pile → splattered puddle with flies).
      aoeOnImpactSpriteUrlAfterStep: '/items/item_shit_puddle.png',
      cleanSprite: true,
      // BETA 14.05 — match orbital sprite size (def.spriteSize=36). User:
      // "all weapon sprites should be identical sizes as it is floating and
      // not shrunk." Default fallback was renderSize*0.75 ≈ 10wu — way smaller.
      spriteDisplaySize: 36,
    },
    maxLevel: 40,
    unlockedByDefault: false,
    unlockQuest: '',
    goopUnlockCost: 0,
    lockOnRange: 260,
    // BETA 13.95: 4s slow on hit (60% speed) — placeholder for the slowing puddle entity (Phase 3).
    applySlowOnHit: 4.0,
    slowOnHitMultiplier: 0.6,
    // BETA 13.96: weapon icon swaps to "fired" sprite (empty pouch slung right) for 0.18s
    // on each shot, then snaps back to "loaded" (poop pulled left). Looks like an actual sling.
      },

  // ─── Banana Bombs — Bigfoot's lobbed explosive bananas (Sorry Boss starter) ───
  {
    id: 'wpn_banana_bombs',
    name: 'Banana Bomb',
    description: 'Bigfoot hurls explosive bananas that splat into a slippery peel field.',
    owner: 'bigfoot',
    weaponType: 'projectile',
    fireMode: 'auto_aim',
    spriteUrl: '/weapons/wpn_banana_bomb_solid.png',
    spriteSize: 40,
    barrelLength: 20,
    baseDamage: 40,
    damagePerLevel: 8,
    baseCritChance: 0.08,
    critChancePerLevel: 0.02,
    baseCritDamage: 0.6,
    critDamagePerLevel: 0.08,
    baseKnockback: 6,
    knockbackPerLevel: 0.4,
    baseProjectileCount: 1,
    projectileCountPerLevel: 0,
    burstDelay: 0,
    baseReloadTime: 1.3,
    reloadTimePerLevel: -0.05,
    minReloadTime: 0.6,
    spreadAngle: 0,
    spreadPerLevel: 0,
    projectile: {
      speed: 300, speedPerLevel: 8,
      size: 15, sizePerLevel: 0.6,
      piercing: 2, piercingPerLevel: 0.5,
      bounces: 0, bouncesPerLevel: 0, bounceRange: 0,
      lifetime: 1.5, spriteUrl: '/items/item_asmons_desk_banana.png',
      trailEffect: true, trailColor: '',
      projectileShape: 'circle', projectileColor: '', projectileGlowColor: '',
      projectileLength: 0, spinRate: 12,
      // slippery peel splat at impact — yellow slow field, no DoT
      aoeOnImpactRadius: 64,
      aoeOnImpactDuration: 4.0,
      aoeOnImpactSlowMult: 0.5,
      aoeOnImpactDotPerSec: 0,
      aoeOnImpactColor: 'rgba(230,205,60,0.45)',
      aoeOnImpactSpriteUrl: '/items/item_asmons_desk_banana_ground.png',
      cleanSprite: true,
      spriteDisplaySize: 38,
    },
    maxLevel: 40,
    unlockedByDefault: false,
    unlockQuest: '',
    goopUnlockCost: 0,
    lockOnRange: 300,
    applySlowOnHit: 3.0,
    slowOnHitMultiplier: 0.6,
  },

  // ─── Squirter — water gun stream ──────────────────────────────
  {
    id: 'wpn_squirter',
    name: 'Squirter',
    description: 'Pink water gun. Continuous stream that soaks enemies WET (4s) — electric weapons hit soaked targets harder.',
    owner: '',
    weaponType: 'projectile',
    fireMode: 'auto_aim',
    spriteUrl: '/weapons/wpn_squirter.png',
    spriteSize: 32,
    barrelLength: 26,
    baseDamage: 17,                // BETA 15.1: 4 → 17. Squirter 3-hit on 50-HP zombie. 0.12s reload = TTK 0.36s (joint-fastest).
    // BETA 20.72 — Dylan-approved (123-run audit): slowest damage scaling in
    // the roster (1/level) arithmetically broke the ≤4-hit law by min 3
    // (6 hits vs the 0.7/min zombie HP ramp). 1 → 2.5 keeps the law intact;
    // Squirter stays a WET-utility weapon, deliberately below median DPS.
    damagePerLevel: 2.5,
    baseCritChance: 0.02,
    critChancePerLevel: 0.01,
    baseCritDamage: 0.3,
    critDamagePerLevel: 0.03,
    baseKnockback: 0,
    knockbackPerLevel: 0,
    baseProjectileCount: 1,
    projectileCountPerLevel: 0,
    burstDelay: 0,
    // BETA 21.38 — Dylan: too fast off the rip. 0.12 (~8.3/sec) → 0.45 (~2.2/sec start);
    // steeper ramp (-0.005 → -0.012) so it still reaches the 0.10 floor (~10/sec) by ~Lv30.
    // BETA 21.40 — steeper ramp so it reaches full rate by ~Lv8 (weapons rarely exceed
    // Lv4-8 in a run); 2.5/s @Lv1 → 10/s @Lv8. Slow level-1 start preserved.
    baseReloadTime: 0.40,
    reloadTimePerLevel: -0.05,
    // BETA 19.89 — full balance audit: at minReloadTime 0.06 + damagePerLevel 1,
    // squirter hit DPS@L9 = 311 (3.1× median weapon DPS = 100). Tier-S overtune.
    // Raised floor to 0.10 → L9 DPS drops to ~187 — still strong, no longer
    // 3× others. Pairs with WET status which is the actual signature.
    minReloadTime: 0.10,
    spreadAngle: 8,
    spreadPerLevel: -0.2,
    projectile: {
      speed: 1400, speedPerLevel: 8,   // BETA 15.1 (squirter): 340 → 1400 (sniper baseline).
      size: 5, sizePerLevel: 0.2,
      piercing: 2, piercingPerLevel: 1,         // BETA 15.24 — Dylan: lowest +1
      bounces: 0, bouncesPerLevel: 0, bounceRange: 0,
      lifetime: 0.6, spriteUrl: '',
      trailEffect: true, trailColor: 'rgba(80,160,255,0.5)',
      projectileShape: 'circle', projectileColor: '#33aaff', projectileGlowColor: '#0066bb',
      projectileLength: 0,
    },
    maxLevel: 40,
    unlockedByDefault: false,
    unlockQuest: '',
    goopUnlockCost: 0,
    lockOnRange: 230,
    // BETA 13.95: WET status (4s) — Phase 3 will add electric-weapon synergy
    applyWetOnHit: 4.0,
  },

  // ─── Frozen Darts — piercing icy darts ────────────────────────
  {
    id: 'wpn_frozen_darts',
    name: 'Brain Freeze',
    description: 'Shoots frozen bullets. High fire rate, low damage. Every hit FREEZES for a 2s slow.',
    owner: '',
    weaponType: 'projectile',
    fireMode: 'auto_aim',
    spriteUrl: '/weapons/wpn_frozen_gun_solid.png',
    spriteSize: 34,
    // BETA 14.10 — calibrator: |dx| 23.09 (sprite faces left in source PNG;
    // barrelLength is positive after the in-game horizontal flip), dy -1.84
    barrelLength: 23.09,
    barrelOffsetY: -1.84,
    baseDamage: 17,                // BETA 15.1: 9 → 17. Frozen Darts 3-hit on 50-HP zombie. (Slow effect on hit retained.)
    // BETA 20.72 — Dylan-approved (123-run audit): 2/level drifted past the
    // ≤4-hit law by min 3 (4.9 hits vs the zombie HP ramp). 2 → 3 keeps the
    // law intact; darts stay a freeze-utility weapon, below median DPS.
    damagePerLevel: 3,
    baseCritChance: 0.08,
    critChancePerLevel: 0.02,
    baseCritDamage: 0.5,
    critDamagePerLevel: 0.05,
    baseKnockback: 1,
    knockbackPerLevel: 0.1,
    baseProjectileCount: 1,
    projectileCountPerLevel: 0,
    burstDelay: 0,
    // BETA 21.37 — Dylan: "frozen darts shoots too fast right off the rip." Was 0.22s
    // (~4.5 shots/sec at level 1) — way too fast to start. 0.22 → 0.50 (2/sec early),
    // and a steeper per-level ramp (-0.008 → -0.012) so it still climbs back to a fast
    // dart gun as you invest levels (reaches the 0.10 floor by ~Lv33).
    // BETA 21.40 — Dylan: weapons only reach Lv2-4 in a real run, so a ramp peaking at
    // Lv33 left them slow forever. Steepened to reach full rate by ~Lv8: 2.5/s @Lv1 →
    // 10/s @Lv8 (focused). Slow level-1 start preserved.
    baseReloadTime: 0.40,
    reloadTimePerLevel: -0.05,
    minReloadTime: 0.10,
    spreadAngle: 3,
    spreadPerLevel: -0.1,
    projectile: {
      // BETA 13.101 — size 6→9 (50% bump per user "increase the size of the
      // projectile by ~10%" request — bumped further so the sprite reads
      // properly without the old colored halo making it look bigger).
      speed: 1400, speedPerLevel: 12,  // BETA 15.1 (frozen_darts): 520 → 1400 (sniper baseline).
      size: 9, sizePerLevel: 0.3,
      piercing: 3, piercingPerLevel: 1,         // BETA 15.24 — Dylan: lowest +1
      bounces: 0, bouncesPerLevel: 0, bounceRange: 0,
      lifetime: 0.8, spriteUrl: '/weapons/wpn_frozen_darts_projectile.png',
      // BETA 13.101 — softer trail (was 0.6 alpha big circles → "way too circular").
      // BETA 20.72 — alpha back up 0.25 → 0.6, but rendered as a thin ICE
      // STREAK line (weaponSystem streak branch), not the 13.101 fat circles.
      // The ghost branch was hijacking this color anyway — dead edit no more.
      trailEffect: true, trailColor: 'rgba(140,210,255,0.6)',
      // Empty colors — no procedural beam fallback to layer over the sprite.
      // User: "the blue around the projectile is way too circular. please
      // just do a light outline glow on it".
      projectileShape: 'circle', projectileColor: '', projectileGlowColor: '',
      projectileLength: 0,
      cleanSprite: true,
      spriteDisplaySize: 22,
    },
    maxLevel: 40,
    unlockedByDefault: false,
    unlockQuest: '',
    goopUnlockCost: 0,
    lockOnRange: 280,
    // BETA 13.95: 2s freeze (slightly less than Ugly Kittens — these are quick darts)
    applyFreezeOnHit: 2.0,
    // BETA 23.05 — spriteFacesLeft REMOVED (Dylan: "the frozen gun is
    // pointing at my character"). The 13.99 flag was for the OLD left-facing
    // art; the 22.76 replacement sprite (wpn_frozen_gun.png) faces RIGHT, so
    // the flip was mirroring correct art backwards — barrel at Greg, muzzle
    // sockets flipped along with it (which is why it still "shot from the
    // tip"). Art verified right-facing pixel-level.
  },

  // ─── Vampire Fang — spinning boomerang blade with lifesteal ─────────────
  // BETA 19.45 — Dylan live audit pivot: "just make drakes fang a throwable
  // that gives you lifesteal. it starts with 3 bounces. that way there is
  // only one slash weapon. i want the blade/sprite to spin and come back
  // to the player. should be around the same speed as the ax but faster."
  // Was an arc/slash melee through BETA 19.44; converted to projectile-
  // throwable so the spatula is the sole melee in the roster. The fang
  // sprite tumbles outbound, bounces between enemies, then boomerangs back
  // to the player — every hit lifesteals 8% (20% with Gas Mask
  // synergy via the existing hasDracsGasMask check in weaponSystem.ts).
  {
    id: 'wpn_dracs_fang',
    name: "Vampire Fang",
    description: 'A thrown scrap-metal chainsaw that saws out and BACK like a boomerang. Bounces between enemies. +8% HP lifesteal per hit.',
    owner: '',
    weaponType: 'projectile',
    fireMode: 'auto_aim',
    spriteUrl: '/weapons/wpn_chainsaw_solid.png',
    spriteSize: 26,             // BETA 22.56 — long chainsaw silhouette (2.25 aspect)
    barrelLength: 24,
    baseDamage: 35,
    damagePerLevel: 6,
    baseCritChance: 0.15,
    critChancePerLevel: 0.03,
    baseCritDamage: 0.6,
    critDamagePerLevel: 0.08,
    baseKnockback: 4,
    knockbackPerLevel: 0.2,
    baseProjectileCount: 1,
    projectileCountPerLevel: 0,
    burstDelay: 0,
    // BETA 20.72 — Dylan-approved (123-run audit): Fang at 0.40× median
    // dmg/min — under the parity floor even role-adjusted for lifesteal.
    // 1.2 → 1.0 (~20% throughput); stays a sustain weapon, not a DPS weapon.
    baseReloadTime: 1.0,
    reloadTimePerLevel: -0.04,
    minReloadTime: 0.5,
    spreadAngle: 0,
    spreadPerLevel: 0,
    projectile: {
      // Dylan: "around the same speed as the ax but faster." Viking Axe
      // outbound speed = 380; Fang = 460 (~20% faster).
      speed: 460, speedPerLevel: 14,
      size: 10, sizePerLevel: 0.25,
      // Dylan: "starts with 3 bounces." Matches Viking Axe's 3-bounce pattern.
      piercing: 0, piercingPerLevel: 0,
      bounces: 3, bouncesPerLevel: 1,
      bounceRange: 240,
      // Returning-phase projectiles are exempt from lifetime expiry (see
      // weaponSystem returning-phase check), so this is the outbound +
      // bounce-chain upper bound. The boomerang always returns regardless.
      lifetime: 6.0,
      spriteUrl: '/weapons/wpn_chainsaw_solid.png',
      // No procedural color trail / glow halo — Dylan: "remove the pretty
      // color animations on it" (preserved from the arc-era).
      // BETA 20.72 — VFX identity law overrides the arc-era note: return leg
      // streaks dark BLOOD-RED (vs Terminator's cyan) via the weaponSystem
      // return-streak branch; outbound tumbles with sprite ghosts. Flagged in
      // beta notes since 19.45 said "remove the pretty color animations".
      trailEffect: true, trailColor: '#CC1122',
      projectileShape: 'circle', projectileColor: '', projectileGlowColor: '',
      projectileLength: 0,
      // Fast tumble so the blade clearly "spins" mid-flight — Dylan: "i
      // want the blade/sprite to spin." Higher than axe's 7 rad/s.
      spinRate: 18,
      // Boomerang behavior: projectile reverses after reach + flies back
      // to the player.
      boomerangReturn: true,
      spriteDisplaySize: 40,    // larger than the orbit icon so the spin reads
      cleanSprite: true,        // skip the procedural glow halo
    },
    maxLevel: 40,
    unlockedByDefault: false,
    unlockQuest: '',
    goopUnlockCost: 0,
    lockOnRange: 320,           // medium-range throw, near Viking Axe's 350
    throwable: true,
    // +8% HP lifesteal per hit — engine reads via _srcDef.lifestealPct in
    // updateProjectiles (weaponSystem.ts:2272) and pipes through
    // state.pendingLifestealHeal. Gas Mask item still 2.5×s this via
    // the hasDracsGasMask synergy check.
    lifestealPct: 0.08,
  },

  // ─── Shitty Cannon — slow AoE cannonball ──────────────────────
  {
    id: 'wpn_shitty_cannon',
    name: 'Poop Shoot',
    description: 'Plumbing-pipe cannon. Slow but heavy poop shell. Impact bursts into a stinking AoE.',
    owner: '',
    weaponType: 'projectile',
    fireMode: 'auto_aim',
    spriteUrl: '/weapons/wpn_poop_shoot_solid.png',
    spriteSize: 38,
    // BETA 14.10 — calibrator: dx 25.33, dy -3.8
    barrelLength: 25.33,
    barrelOffsetY: -3.8,
    baseDamage: 37,
    damagePerLevel: 9,
    baseCritChance: 0.05,
    critChancePerLevel: 0.02,
    baseCritDamage: 0.6,
    critDamagePerLevel: 0.08,
    baseKnockback: 8,
    knockbackPerLevel: 0.5,
    baseProjectileCount: 1,
    projectileCountPerLevel: 0,
    burstDelay: 0,
    baseReloadTime: 1.8,
    reloadTimePerLevel: -0.06,
    minReloadTime: 0.9,
    spreadAngle: 0,
    spreadPerLevel: 0,
    projectile: {
      speed: 1400, speedPerLevel: 6,   // BETA 15.1 (shitty_cannon): 240 → 1400 (sniper baseline). Heavy cannon shell flies fast.
      size: 18, sizePerLevel: 0.7,
      piercing: 0, piercingPerLevel: 0,
      bounces: 0, bouncesPerLevel: 0, bounceRange: 0,
      lifetime: 1.6, spriteUrl: '/weapons/wpn_shitty_cannon_projectile.png',
      // BETA 14.08 — trail OFF per user "vfx on projectile shitty cannon. remove".
      // BETA 20.72 — trail back ON via sprite GHOSTS, tuned HEAVY in the
      // weaponSystem ghost branch (0.55 alpha, slow shrink) — the dense
      // cannonball smear vs Poop Sling's light 0.35 lob. (14.08 removed the
      // old generic colored trail, not this.)
      trailEffect: true, trailColor: '',
      // Empty colors → no fake-brown-circle orb fallback.
      projectileShape: 'circle', projectileColor: '', projectileGlowColor: '',
      projectileLength: 0, spinRate: 8,
      // BETA 13.97 — smoke cloud at impact: 80wu radius, 3s, 60% slow + 12 dps DoT
      aoeOnImpactRadius: 80,
      aoeOnImpactDuration: 3.0,
      aoeOnImpactSlowMult: 0.6,
      aoeOnImpactDotPerSec: 12,
      aoeOnImpactColor: 'rgba(120,120,110,0.0)',
      aoeOnImpactSpriteUrl: '/items/item_poop_deck_ground.png',
      // BETA 14.60 — stage-2 sprite swap on first enemy contact. Reuses
      // the existing item_shit stage-2 sprite for consistent visual language
      // across all poop content (poop pile → splattered puddle with flies).
      aoeOnImpactSpriteUrlAfterStep: '/items/item_shit_puddle.png',
      cleanSprite: true,
      // BETA 20.76 — REVERTED 46 → 38. Dylan's law (2026-06-12): "if i made
      // a sprite for them the sprite was not to be messed with." The 20.72
      // agent resized his cannonball sprite to read "heavier" — that's his
      // art, his call, not ours. The heavy IDENTITY stays via the dense
      // ghost trail (decoration around the sprite is fine; the sprite isn't).
      spriteDisplaySize: 38,
    },
    maxLevel: 40,
    unlockedByDefault: false,
    unlockQuest: '',
    goopUnlockCost: 0,
    lockOnRange: 260,
    // BETA 13.95: 4s slow (50% speed) — Phase 3 will add the persistent puddle entity
    applySlowOnHit: 4.0,
    slowOnHitMultiplier: 0.5,
  },

  // ─── The Ex Girlfriend — 5-shot piercing fan ──────────────────
  {
    id: 'wpn_ex_girlfriend',
    name: 'The Ex Girlfriend',
    description: 'Fires 5 piercing projectiles in a fan pattern.',
    owner: '',
    weaponType: 'projectile',
    fireMode: 'auto_aim',
    spriteUrl: '/weapons/wpn_ex_girlfriend_solid.png',
    spriteSize: 36,
    // BETA 14.10 — calibrator: 5-barrel fan (sprite faces left; |dx| values
    // range 18.3-23.55, center barrel #3 at |dx| 23.55, dy 0). Barrels span
    // dy -10.65 to +11.4 = ~22wu vertical = atan2(11.4/22.05) × 2 ≈ 60° fan.
    // baseProjectileCount stays 5 (matches catalog); spreadAngle bumped 28→60.
    barrelLength: 23.55,
    baseDamage: 17,                // BETA 15.1: 14 → 17. Ex Girlfriend per-pellet (5 in fan). Each pellet 3-hit on 50-HP zombie; full fan landing = 85 dmg.
    damagePerLevel: 2.5,
    baseCritChance: 0.08,
    critChancePerLevel: 0.02,
    baseCritDamage: 0.5,
    critDamagePerLevel: 0.05,
    baseKnockback: 2,
    knockbackPerLevel: 0.2,
    baseProjectileCount: 5,
    projectileCountPerLevel: 1,    // BETA 15.24 — Dylan: lowest +1
    // BETA 15.77 — Per Dylan: "the gun should shoot 1,2,3,4,5 pretty quickly
    // back to back" with each shot triggering the ex_girlfriend_fire.mp3
    // sample. burstDelay 0 → 0.08s so the 5 projectiles fire sequentially
    // over 0.4s rather than all-at-once. Each shot's ammo decrement triggers
    // playExGirlfriendFire() which uses a fresh BufferSource each call so
    // the 5 sample plays naturally overlap without cutting each other off.
    burstDelay: 0.08,
    baseReloadTime: 1.0,
    reloadTimePerLevel: -0.04,
    minReloadTime: 0.5,
    // BETA 14.10 — bumped 28 → 60 to match the calibrator-marked 5-barrel
    // visual fan on the Ex Girlfriend sprite (each barrel ≈12-15° apart).
    spreadAngle: 60,
    spreadPerLevel: -0.4,
    projectile: {
      speed: 1400, speedPerLevel: 10,  // BETA 15.1 (ex_girlfriend): 440 → 1400 (sniper baseline).
      size: 7, sizePerLevel: 0.3,
      piercing: 3, piercingPerLevel: 1,         // BETA 15.24 — Dylan: lowest +1
      bounces: 0, bouncesPerLevel: 0, bounceRange: 0,
      lifetime: 0.8, spriteUrl: '',
      trailEffect: true, trailColor: 'rgba(255,80,180,0.65)', // BETA 20.72 — 0.4 → 0.65: the pink fan now reads as 5 hot streaks
      projectileShape: 'beam', projectileColor: '#ff6699', projectileGlowColor: '#cc3366',
      projectileLength: 14,
    },
    maxLevel: 40,
    unlockedByDefault: false,
    unlockQuest: '',
    goopUnlockCost: 0,
    lockOnRange: 280,
    // BETA 23.05 — spriteFacesLeft REMOVED (same stale-flag bug as frozen
    // darts: 13.99 flag, but the 22.76 replacement art faces RIGHT).
  },

  // ─── Bad Breakfast — toaster gun (fires scalding burnt toast + zap) ───
  {
    id: 'wpn_hurray',
    name: 'Bad Breakfast',
    description: 'Chrome toaster-gun hybrid. Wide spread of scalding breakfast, brief disorienting slow on hit.',
    owner: '',
    weaponType: 'projectile',
    fireMode: 'auto_aim',
    spriteUrl: '/weapons/wpn_toaster_gun_solid.png',
    spriteSize: 36,
    // BETA 14.10 — calibrator: dx 20.25, dy -2.25
    barrelLength: 20.25,
    barrelOffsetY: -2.25,
    // BETA 16.91 — PSBOT cycle-30k §11B + Dylan "yes": Hurray was the
    // underperformer (avg 22 dmg/hit, lowest in the bot CSV). Bumped
    // baseDamage 17 → 22 + damagePerLevel 1.5 → 2.0 + spread 60° → 50°
    // (more pellets land per shot). Now per-pellet ~22 base, level 5
    // ~30 base, with tighter cone making cluster shots more reliable.
    // Should land in the 60-90 dmg/hit band post-change.
    baseDamage: 22,
    damagePerLevel: 2.0,
    baseCritChance: 0.10,
    critChancePerLevel: 0.02,
    baseCritDamage: 0.4,
    critDamagePerLevel: 0.05,
    baseKnockback: 1,
    knockbackPerLevel: 0.1,
    baseProjectileCount: 8,
    projectileCountPerLevel: 1,
    burstDelay: 0,
    baseReloadTime: 0.9,
    reloadTimePerLevel: -0.04,
    minReloadTime: 0.4,
    spreadAngle: 50,
    spreadPerLevel: -0.5,
    projectile: {
      speed: 1400, speedPerLevel: 8,   // BETA 15.1 (hurray): 360 → 1400 (sniper baseline).
      size: 5, sizePerLevel: 0.2,
      piercing: 0, piercingPerLevel: 0,
      bounces: 0, bouncesPerLevel: 0, bounceRange: 0,
      lifetime: 0.7, spriteUrl: '',
      trailEffect: true, trailColor: 'rgba(255,200,80,0.4)',
      projectileShape: 'circle', projectileColor: '#ffcc44', projectileGlowColor: '#ff8800',
      projectileLength: 0,
    },
    maxLevel: 40,
    unlockedByDefault: false,
    unlockQuest: '',
    goopUnlockCost: 0,
    lockOnRange: 200,
    // BETA 13.95: 1s brief disorient (slow to 60%)
    applySlowOnHit: 1.0,
    slowOnHitMultiplier: 0.6,
  },
  // ═══════════════════════════════════════════════════════════════
  //  WEAPON SYSTEM v2 — PHASE 2 (BETA 22.58)
  //  Five new standard weapons. ALL numbers are first-draft (Dylan
  //  tunes after play). unlockedByDefault:false keeps every one of
  //  them OUT of level-up/drop pools until its quest is completed —
  //  per Dylan: audit in bench + in-game first, pools after.
  // ═══════════════════════════════════════════════════════════════

  // ─── The Intern — orange & blue SMG. Fires fast, hits soft. ───
  {
    id: 'wpn_smg',
    name: 'The Intern',
    description: 'Overeager burst of tiny bullets. Fires fast, hits soft, never stops trying.',
    owner: '',
    weaponType: 'projectile',
    fireMode: 'auto_aim',
    spriteUrl: '/weapons/wpn_smg_solid.png',
    spriteSize: 26,
    barrelLength: 20,
    barrelOffsetY: -2,
    baseDamage: 7,
    damagePerLevel: 1.5,
    baseCritChance: 0.06,
    critChancePerLevel: 0.02,
    baseCritDamage: 0.5,
    critDamagePerLevel: 0.05,
    baseKnockback: 2,
    knockbackPerLevel: 0.2,
    baseProjectileCount: 5,      // 5-round burst — the identity
    projectileCountPerLevel: 0.25,
    burstDelay: 0.055,
    baseReloadTime: 0.85,
    reloadTimePerLevel: -0.02,
    minReloadTime: 0.4,
    spreadAngle: 9,
    spreadPerLevel: -0.3,
    projectile: {
      speed: 720, speedPerLevel: 10,
      size: 4, sizePerLevel: 0.1,
      piercing: 0, piercingPerLevel: 0,
      bounces: 0, bouncesPerLevel: 0, bounceRange: 0,
      lifetime: 0.6, spriteUrl: '',
      trailEffect: true, trailColor: '#ff9a3d',
      projectileShape: 'bullet', projectileColor: '#ff9a3d', projectileGlowColor: '#ffcf7a',
      projectileLength: 10, spinRate: 0,
    },
    maxLevel: 40,
    unlockedByDefault: false,
    unlockQuest: 'Kill 750 enemies total',
    goopUnlockCost: 0,
    lockOnRange: 340,
  },

  // ─── RPG-7 — slow rocket, huge blast (reuses explodeOnImpact) ───
  {
    id: 'wpn_rpg',
    name: 'RPG-7',
    description: 'Battle-worn thumper of last resort. Slow rocket, enormous blast.',
    owner: '',
    weaponType: 'projectile',
    fireMode: 'auto_aim',
    spriteUrl: '/weapons/wpn_rpg_solid.png',
    spriteSize: 20,
    barrelLength: 34,
    baseDamage: 55,
    damagePerLevel: 9,
    baseCritChance: 0.05,
    critChancePerLevel: 0.02,
    baseCritDamage: 0.6,
    critDamagePerLevel: 0.06,
    baseKnockback: 26,
    knockbackPerLevel: 1,
    baseProjectileCount: 1,
    projectileCountPerLevel: 0,
    burstDelay: 0,
    baseReloadTime: 2.5,
    reloadTimePerLevel: -0.06,
    minReloadTime: 1.3,
    spreadAngle: 0,
    spreadPerLevel: 0,
    projectile: {
      speed: 340, speedPerLevel: 8,
      size: 12, sizePerLevel: 0.3,
      piercing: 0, piercingPerLevel: 0,
      bounces: 0, bouncesPerLevel: 0, bounceRange: 0,
      lifetime: 2.2, spriteUrl: '',
      trailEffect: true, trailColor: '#9a9a9a',   // smoke trail
      projectileShape: 'orb', projectileColor: '#c96a2c', projectileGlowColor: '#ffb36b',
      projectileLength: 0, spinRate: 0,
      explodeOnImpactRadius: 130,
      explodeOnImpactDamageMult: 1.0,
    },
    maxLevel: 40,
    unlockedByDefault: false,
    unlockQuest: 'Kill 5 bosses total',
    goopUnlockCost: 0,
    lockOnRange: 460,
  },

  // ─── Tactical Crossbow — silent pierce bolt, crit machine ───
  {
    id: 'wpn_crossbow',
    name: 'Tactical Crossbow',
    description: 'Cracked composite crossbow. Silent bolts that punch through the line and love to crit.',
    owner: '',
    weaponType: 'projectile',
    fireMode: 'auto_aim',
    spriteUrl: '/weapons/wpn_crossbow_solid.png',
    spriteSize: 24,
    barrelLength: 32,
    baseDamage: 44,
    damagePerLevel: 7,
    baseCritChance: 0.25,
    critChancePerLevel: 0.03,
    baseCritDamage: 0.8,
    critDamagePerLevel: 0.08,
    baseKnockback: 8,
    knockbackPerLevel: 0.4,
    baseProjectileCount: 1,
    projectileCountPerLevel: 0,
    burstDelay: 0,
    baseReloadTime: 1.5,
    reloadTimePerLevel: -0.04,
    minReloadTime: 0.7,
    spreadAngle: 0,
    spreadPerLevel: 0,
    projectile: {
      speed: 950, speedPerLevel: 15,
      size: 6, sizePerLevel: 0.1,
      piercing: 3, piercingPerLevel: 0.5,
      bounces: 0, bouncesPerLevel: 0, bounceRange: 0,
      lifetime: 0.9, spriteUrl: '',
      trailEffect: true, trailColor: '#8fa8b8',
      projectileShape: 'bullet', projectileColor: '#cfd6dd', projectileGlowColor: '#8fa8b8',
      projectileLength: 18, spinRate: 0,
    },
    maxLevel: 40,
    unlockedByDefault: false,
    unlockQuest: 'Deal 100,000 damage total',
    goopUnlockCost: 0,
    lockOnRange: 520,
  },

  // ─── Chain Whip — long fast melee sweep that snaps back ───
  {
    id: 'wpn_chain_whip',
    name: 'Chain Whip',
    description: 'Dark steel chain whip. Long, fast sweeps in the direction you move — longer reach, tighter arc than the bat.',
    owner: '',
    weaponType: 'melee',
    fireMode: 'auto_direction',
    arc: true,
    spriteUrl: '/weapons/wpn_chain_whip_solid.png',
    spriteSize: 58,
    barrelLength: 0,
    baseDamage: 22,
    damagePerLevel: 3.5,
    baseCritChance: 0.12,
    critChancePerLevel: 0.05,
    baseCritDamage: 0.5,
    critDamagePerLevel: 0.08,
    baseKnockback: 6,
    knockbackPerLevel: 0.4,
    baseProjectileCount: 1,
    projectileCountPerLevel: 0,
    burstDelay: 0,
    baseReloadTime: 0.42,
    reloadTimePerLevel: -0.012,
    minReloadTime: 0.24,
    spreadAngle: 70,
    spreadPerLevel: 9,
    projectile: {
      speed: 900,              // 900 × 0.10 = 90wu reach — outranges the bat
      speedPerLevel: 12,
      size: 7, sizePerLevel: 0.2,
      piercing: 99, piercingPerLevel: 0,
      bounces: 0, bouncesPerLevel: 0, bounceRange: 0,
      lifetime: 0.10, spriteUrl: '',
      trailEffect: false, trailColor: '',
      projectileShape: 'circle', projectileColor: '', projectileGlowColor: '',
      projectileLength: 0,
    },
    maxLevel: 40,
    unlockedByDefault: false,
    unlockQuest: 'Take 1,000 damage total',
    goopUnlockCost: 0,
    lockOnRange: 90,
    arcLiveDirection: true,
    arcSpriteSizePerLevel: 1.4,
    arcMaxSpreadDeg: 240,
  },

  // ─── Railgun — charged copper pierce-line (Ultra Beam's angry cousin) ───
  {
    id: 'wpn_railgun',
    name: 'Railgun',
    description: 'Charged rail coils. Winds up, then puts a searing white-hot blue rail line through everything at once.',
    owner: '',
    weaponType: 'projectile',
    fireMode: 'auto_aim',
    spriteUrl: '/weapons/wpn_railgun_solid.png',
    spriteSize: 30,
    barrelLength: 34,
    barrelOffsetY: -2,
    baseDamage: 80,
    damagePerLevel: 12,
    baseCritChance: 0.12,
    critChancePerLevel: 0.03,
    baseCritDamage: 0.7,
    critDamagePerLevel: 0.06,
    baseKnockback: 12,
    knockbackPerLevel: 0.5,
    baseProjectileCount: 1,
    projectileCountPerLevel: 0,
    burstDelay: 0,
    baseReloadTime: 2.8,
    reloadTimePerLevel: 0,
    minReloadTime: 1.2,
    chargeUpMs: 500,
    spreadAngle: 0,
    spreadPerLevel: 0,
    projectile: {
      speed: 8000, speedPerLevel: 0,
      size: 8, sizePerLevel: 0,
      piercing: 999, piercingPerLevel: 0,
      bounces: 0, bouncesPerLevel: 0, bounceRange: 0,
      lifetime: 1.0, spriteUrl: '',
      trailEffect: true, trailColor: '#4aa8ff',    // BETA 22.76 — electric BLUE rail (Bible: white-hot core + dirty blue edges), never Ultra Beam purple/copper
      projectileShape: 'beam', projectileColor: '#4aa8ff', projectileGlowColor: '#7fc4ff',
      projectileLength: 100, spinRate: 0,
      cleanSprite: false,
    },
    maxLevel: 40,
    unlockedByDefault: false,
    unlockQuest: 'Kill 2,000 enemies total',
    goopUnlockCost: 0,
    lockOnRange: 900,
  },];

// ═══════════════════════════════════════════════════════════════
//  HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════

/** Look up a weapon definition by ID */
export function getWeaponDef(id: string): WeaponDefinition | undefined {
  return WEAPON_DATABASE.find(w => w.id === id);
}

/** Returns true if this weapon is throwable (sprite IS the projectile — no ammo counter) */
export function isWeaponThrowable(id: string): boolean {
  return WEAPON_DATABASE.find(w => w.id === id)?.throwable === true;
}

/** Returns true if this weapon is an aura (ring around player — no ammo counter) */
export function isWeaponAura(id: string): boolean {
  return WEAPON_DATABASE.find(w => w.id === id)?.aura === true;
}

/** Returns true if this weapon is an arc melee (directional fan slash — no ammo counter) */
export function isWeaponArc(id: string): boolean {
  return WEAPON_DATABASE.find(w => w.id === id)?.arc === true;
}

/** Get the weapon definition for a character's starter weapon */
export function getStarterWeapon(characterId: string): WeaponDefinition | undefined {
  return WEAPON_DATABASE.find(w => w.owner === characterId);
}

// BETA 19.2/19.4 — Megabonk card rarity multipliers shared by computeWeaponStats
// and the card builder.
const CARD_RARITY_MULT: Record<import('./peanutPercs').UpgradeRarity, number> = {
  common: 1.0, uncommon: 1.2, rare: 1.4, epic: 1.6, legendary: 2.0,
};

/** BETA 19.14 — Luck-driven magnitude multiplier for weapon level-up
 *  card stat deltas. Per Dylan: "all of these numbers should be higher
 *  or lower based on luck... i want a perfect ratio of growth for luck
 *  to get you better rolls for weapon buffs".
 *
 *  Curve (clean linear, capped):
 *    luck    0  →  1.0× (baseline)
 *    luck   50  →  1.25×
 *    luck  100  →  1.5×
 *    luck  150  →  1.75×
 *    luck  200+ →  2.0×  (cap — combined with legendary 2.0× rarity = 4.0× peak)
 *
 *  Combined with the existing rarity-tier curve (more legendaries at
 *  high luck) AND the per-stat floors that skip stats below a meaningful
 *  threshold, this gives a smooth growth where every point of luck
 *  visibly buffs every weapon card a little more. */
export function luckMagnitudeMult(luck: number = 0): number {
  const clamped = Math.max(0, Math.min(luck, 200));
  return 1 + clamped / 200;
}

/** BETA 19.4 — Derive a default level-up stat pool from a weapon's
 *  non-zero `*PerLevel` fields. Used when a weapon doesn't define an
 *  explicit `levelUpStatPool`. Ensures every weapon has a sensible pool
 *  without hand-curating all 20 weapons. */
export function getWeaponStatPool(def: WeaponDefinition): WeaponStatKey[] {
  if (def.levelUpStatPool && def.levelUpStatPool.length > 0) return def.levelUpStatPool;
  const pool: WeaponStatKey[] = [];
  if (def.damagePerLevel > 0)             pool.push('damage');
  if (def.critChancePerLevel > 0)         pool.push('critChance');
  if (def.critDamagePerLevel > 0)         pool.push('critDamage');
  if (def.knockbackPerLevel > 0)          pool.push('knockback');
  if (def.reloadTimePerLevel < 0)         pool.push('reload'); // faster reload = good
  if (def.projectileCountPerLevel > 0)    pool.push('projectileCount');
  if (def.spreadPerLevel < 0)             pool.push('spread'); // tighter spread = good
  if (def.projectile.speedPerLevel > 0)   pool.push('projectileSpeed');
  if (def.projectile.sizePerLevel > 0)    pool.push('projectileSize');
  if (def.projectile.piercingPerLevel > 0) pool.push('projectilePiercing');
  if (def.projectile.bouncesPerLevel > 0) pool.push('projectileBounces');
  // Safety: if a weapon has zero scaling fields, default to damage so the
  // card still has something to show.
  if (pool.length === 0) pool.push('damage');
  return pool;
}

/** Calculate computed stats for a weapon at a given level and rarity.
 *
 *  BETA 19.2 — Added per-level rarity scaling via `cardRarityHistory`.
 *  BETA 19.4 — Added per-level stat-pool selection via `cardRollsHistory`.
 *  When `cardRollsHistory` is present, each level only applies the
 *  `*PerLevel` delta for the stats that ROLLED at that level (Megabonk
 *  style). When absent, falls back to `cardRarityHistory` (all stats
 *  scale by rarity, no stat selection). When both absent, legacy uniform
 *  per-level scaling. */
export function computeWeaponStats(
  def: WeaponDefinition,
  level: number,
  rarity: WeaponRarity = 'common',
  cardRarityHistory?: import('./peanutPercs').UpgradeRarity[],
  cardRollsHistory?: WeaponCardRoll[],
): {
  damage: number;
  critChance: number;
  critDamage: number;
  knockback: number;
  projectileCount: number;
  reloadTime: number;
  spreadAngle: number;
  projectileSpeed: number;
  projectileSize: number;
  projectilePiercing: number;
  projectileBounces: number;
} {
  const lvl = level - 1; // Level 1 = base stats, no scaling
  const rarityMult = RARITY_MULTIPLIERS[rarity];

  // BETA 19.4 — Per-stat accumulators. Each stat's accumulated scale is
  // the sum of rarity multipliers for the levels at which that stat
  // rolled. When cardRollsHistory is present, ONLY rolled stats accumulate.
  // When only cardRarityHistory is present (19.2 saves), ALL stats
  // accumulate via the per-level rarity (legacy behavior).
  const acc: Record<WeaponStatKey, number> = {
    damage: 0, critChance: 0, critDamage: 0, knockback: 0, reload: 0,
    projectileCount: 0, spread: 0, projectileSpeed: 0, projectileSize: 0,
    projectilePiercing: 0, projectileBounces: 0,
  };

  if (cardRollsHistory && cardRollsHistory.length > 0) {
    // 19.4 path: per-stat per-level history.
    // BETA 19.14: also multiply by the luck-magnitude mult captured at
    // roll time (defaults to 1.0 for pre-19.14 saves).
    for (let i = 1; i <= lvl; i++) {
      const roll = cardRollsHistory[i];
      if (!roll) continue;
      const rarityM = CARD_RARITY_MULT[roll.rarity] ?? 1.0;
      const luckM = roll.luckMult ?? 1.0;
      const jitterM = roll.jitterMult ?? 1.0; // BETA 21.02 — per-card buff-variety jitter (1.0 for old saves)
      const mult = rarityM * luckM * jitterM;
      for (const stat of roll.stats) {
        acc[stat] += mult;
      }
    }
  } else {
    // 19.2 fallback: all stats scale by per-level rarity.
    for (let i = 1; i <= lvl; i++) {
      const r = cardRarityHistory?.[i] ?? 'common';
      const mult = CARD_RARITY_MULT[r] ?? 1.0;
      (Object.keys(acc) as WeaponStatKey[]).forEach(k => { acc[k] += mult; });
    }
  }

  const damage = Math.round((def.baseDamage + def.damagePerLevel * acc.damage) * rarityMult);
  const critChance = Math.min(1.0, def.baseCritChance + def.critChancePerLevel * acc.critChance);
  const critDamage = def.baseCritDamage + def.critDamagePerLevel * acc.critDamage;
  const knockback = (def.baseKnockback + def.knockbackPerLevel * acc.knockback) * rarityMult;

  const projectileCount = def.baseProjectileCount + Math.floor(def.projectileCountPerLevel * acc.projectileCount);

  const reloadTime = Math.max(
    def.minReloadTime,
    def.baseReloadTime + def.reloadTimePerLevel * acc.reload,
  );

  const spreadAngle = Math.max(0, def.spreadAngle + def.spreadPerLevel * acc.spread);

  const projectileSpeed = (def.projectile.speed + def.projectile.speedPerLevel * acc.projectileSpeed) * rarityMult;
  const projectileSize = def.projectile.size + def.projectile.sizePerLevel * acc.projectileSize;
  const projectilePiercing = def.projectile.piercing + Math.floor(def.projectile.piercingPerLevel * acc.projectilePiercing);
  const projectileBounces = def.projectile.bounces + Math.floor(def.projectile.bouncesPerLevel * acc.projectileBounces);

  // BETA 22.42 — fold socketed weapon-mod buffs in last, but ONLY when this weapon
  // is armed ("Modded") in the loadout. Standard guns return untouched. The mod
  // helper is delta-safe: previews call this at level N and N+1, so the mod term
  // cancels in the displayed card delta.
  return applyModBuffs({
    damage,
    critChance,
    critDamage,
    knockback,
    projectileCount,
    reloadTime,
    spreadAngle,
    projectileSpeed,
    projectileSize,
    projectilePiercing,
    projectileBounces,
  }, def.id);
}

/** Create a fresh weapon instance at level 1 */
export function createWeaponInstance(
  definitionId: string,
  rarity: WeaponRarity = 'common',
): WeaponInstance | null {
  const def = getWeaponDef(definitionId);
  if (!def) return null;

  const stats = computeWeaponStats(def, 1, rarity);

  return {
    definitionId,
    level: 1,
    rarity,
    // BETA 19.2 — Initialize per-level rarity history.
    cardRarityHistory: ['common'],
    // BETA 19.4 — Initialize per-level full roll history. Index 0 is the
    // base level (empty stats). Pushed onto when levelUpWeapon is called
    // with a card rarity + stats. Each entry says "this level rolled
    // rarity R and stat(s) [S]" so computeWeaponStats can apply per-stat
    // deltas only for stats actually rolled.
    cardRollsHistory: [{ rarity: 'common', stats: [] }],

    ...stats,

    currentAmmo: stats.projectileCount,
    reloadTimer: 0,
    burstTimer: 0,
    isFiring: true,
    isReloading: false,
    // Per-weapon telemetry
    totalDamageDealt: 0,
    totalShotsFired: 0,
    totalShotsHit: 0,
    totalCrits: 0,
    totalCritRolls: 0,    // BETA 14.30 (BUG-01)
    totalTickEvents: 0,   // BETA 14.30 (BUG-03/04)

    aimAngle: 0,
    hasTarget: false,

    // Aura state — first pulse fires after one full cooldown
    auraTick: stats.reloadTime,
    auraPulseTimer: 0,
    firedFlashTimer: 0,
    recoilTimer: 0,
    idleBobPhase: Math.random() * Math.PI * 2,
    contactCooldowns: new Map(),
    // BETA 15.74 — Ultra Beam charge-up
    chargeUpRemaining: 0,
    chargeUpLockedAimAngle: 0,
  };
}

/** Level up a weapon instance, recalculating all stats.
 *  BETA 19.2 — Per-level rarity history.
 *  BETA 19.4 — Per-level stat-roll history. `cardStats` is the array of
 *  stats that rolled on the card the player picked. Defaults to all
 *  stats from the weapon's pool (so legacy callers behave like the old
 *  "all stats scale" model). */
export function levelUpWeapon(
  instance: WeaponInstance,
  cardRarity: import('./peanutPercs').UpgradeRarity = 'common',
  cardStats?: WeaponStatKey[],
  cardLuckMult?: number,
  cardJitterMult?: number,
): boolean {
  const def = getWeaponDef(instance.definitionId);
  if (!def) return false;
  if (instance.level >= def.maxLevel) return false;

  // BETA 19.39 — OFF-BY-ONE FIX. Was: `instance.level++` THEN
  // `cardRollsHistory[instance.level] = ...`. That wrote the new card
  // at index N+1 after incrementing, but computeWeaponStats reads
  // `for i=1; i<=lvl=level-1` → for a weapon at level N+1 it reads
  // indices 1..N, NEVER index N+1. So the most-recently-picked card
  // was always one level behind from being applied. Visible symptom:
  // first-ever level-up card showed "MAXED" because _projectStats saw
  // no delta. Fix: write at the OLD level (pre-increment) so
  // `cardRollsHistory[N]` = "card picked to leave level N", matching
  // the reader's `i=1..level-1` (apply all cards before reaching this
  // level). instance.level++ moves AFTER the writes.
  if (!instance.cardRarityHistory) instance.cardRarityHistory = ['common'];
  instance.cardRarityHistory[instance.level] = cardRarity;

  if (!instance.cardRollsHistory) instance.cardRollsHistory = [{ rarity: 'common', stats: [] }];
  const _statsToCommit: WeaponStatKey[] = cardStats ?? getWeaponStatPool(def);
  const _luckMultToCommit = cardLuckMult ?? 1.0;
  const _jitterMultToCommit = cardJitterMult ?? 1.0; // BETA 21.02 — persist the offer-time jitter
  instance.cardRollsHistory[instance.level] = {
    rarity: cardRarity,
    stats: _statsToCommit,
    luckMult: _luckMultToCommit,
    jitterMult: _jitterMultToCommit,
  };

  // Increment AFTER writing so the writes land at the OLD level index.
  instance.level++;

  const stats = computeWeaponStats(def, instance.level, instance.rarity, instance.cardRarityHistory, instance.cardRollsHistory);

  instance.damage = stats.damage;
  instance.critChance = stats.critChance;
  instance.critDamage = stats.critDamage;
  instance.knockback = stats.knockback;
  instance.projectileCount = stats.projectileCount;
  instance.reloadTime = stats.reloadTime;
  instance.spreadAngle = stats.spreadAngle;
  instance.projectileSpeed = stats.projectileSpeed;
  instance.projectileSize = stats.projectileSize;
  instance.projectilePiercing = stats.projectilePiercing;
  instance.projectileBounces = stats.projectileBounces;

  // Refill ammo on level-up
  instance.currentAmmo = stats.projectileCount;
  instance.reloadTimer = 0;
  instance.burstTimer = 0;
  instance.isFiring = true;
  instance.isReloading = false;

  return true;
}

/** Upgrade weapon rarity (GOOP system), recalculate stats */
export function upgradeWeaponRarity(
  instance: WeaponInstance,
  newRarity: WeaponRarity,
): void {
  instance.rarity = newRarity;
  const def = getWeaponDef(instance.definitionId);
  if (!def) return;

  const stats = computeWeaponStats(def, instance.level, newRarity);
  instance.damage = stats.damage;
  instance.critChance = stats.critChance;
  instance.critDamage = stats.critDamage;
  instance.knockback = stats.knockback;
  instance.projectileSpeed = stats.projectileSpeed;
  instance.projectileSize = stats.projectileSize;
}

/** Reset weapon rarity back to common (on death before boss portal) */
export function resetWeaponRarity(instance: WeaponInstance): void {
  upgradeWeaponRarity(instance, 'common');
}
